import React, { useMemo, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation, Link } from "wouter";
import { useAuth, CLIENT_AUTH_KEY } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { ETAPAS_PADRAO, ETAPA_COR, etapaInicial, rotuloEtapa, estaNoQuadro } from "@shared/etapas";
import { useQuadro } from "@/hooks/useQuadro";
import ImportarExportarModal from "@/components/ImportarExportarModal";

// ─── Types ────────────────────────────────────────────────────────────────────

type Rede    = "instagram" | "tiktok" | "youtube" | "linkedin" | "twitter" | "multiplas";
type Formato = "feed" | "reels" | "stories" | "carrossel" | "video" | "post" | "thread" | "comentario";
type Coluna  =
  | "sugestao_tema"
  | "ideia" | "captacao" | "roteiro" | "producao" | "producao_criacao" | "revisao" | "revisao_design"
  | "ajuste" | "aprovado" | "agendado" | "publicado" | "desaprovado";

// Subset de Coluna sem sugestao_tema — usado no backend pautas.move,
// no modal de criar pauta e nos filtros. sugestao_tema é etapa de TEMA,
// não de pauta, então nunca chega aqui em runtime.
type ColunaPauta = Exclude<Coluna, "sugestao_tema">;

export interface Pauta {
  id: number;
  clientId: number;
  titulo: string;
  descricao?: string | null;
  rede: Rede;
  formato: Formato;
  coluna: Coluna;
  responsavel?: string | null;
  dataPrevista?: string | null;
  contentId?: string | null;
  linkDrive?: string | null;
  linkClickup?: string | null;
  tags: string[];
  ordem: number;
  linhaEditorial?: string | null;
  territorio?: string | null;
  objetivo?: string | null;
  direcionamento?: string | null;
  segmento?: string | null;
  persona?: string | null;
  funil?: "T" | "M" | "F" | null;
  kpiMacro?: string | null;
  gancho?: string | null;
  prazoEntrega?: string | null;
  perfilAlvo?: string | null;
  horarioPostagem?: string | null;
  legenda?: string | null;
  observacoes?: string | null;
  motivoRecusa?: string | null;
  // Peças relacionadas — story de engajamento / comentário de porta-voz
  // atrelados a um post-mãe (parentId). portaVoz + anguloComentario só p/ comentário.
  parentId?: number | null;
  portaVoz?: string | null;
  anguloComentario?: string | null;
  createdAt: number;
  updatedAt: number;
  createdBy?: string | null;
  deletedAt?: number | null;
  // Comentários agregados (preenchidos pelo backend em list/get)
  commentCount?: number;
  lastCommentBy?: string | null;
  lastCommentAvatar?: string | null;
  lastCommentAt?: number | null;
  recentCommenters?: string[];
}

// ─── Modal de recusa (Desaprovado) — motivo obrigatório ─────────────────────────
// Pedido Sophia/EmiteAí (jul/26): ao desaprovar, abrir pop-up pedindo o porquê,
// pra não ter esquecimento. O motivo fica no card e alimenta o Direcionador.
function DesaprovarModal({
  titulo, onConfirm, onCancel, pending,
}: {
  titulo?: string;
  onConfirm: (motivo: string) => void;
  onCancel: () => void;
  pending?: boolean;
}) {
  const [motivo, setMotivo] = useState("");
  const podeConfirmar = motivo.trim().length > 0 && !pending;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
         onClick={onCancel}>
      <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-lg">🚫</span>
          <h3 className="text-base font-semibold text-[#0A0A0A]">Recusar conteúdo</h3>
        </div>
        {titulo && <p className="text-xs text-zinc-500 mb-3 line-clamp-2">"{titulo}"</p>}
        <label className="block text-xs font-medium text-zinc-600 mb-1.5">
          Motivo da recusa <span className="text-red-500">*</span>
        </label>
        <textarea
          autoFocus
          value={motivo}
          onChange={e => setMotivo(e.target.value)}
          rows={3}
          placeholder="Explique por que este conteúdo está sendo recusado (fica registrado no card e no Direcionador)."
          className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm text-[#0A0A0A] focus:border-red-400 focus:outline-none focus:ring-1 focus:ring-red-400"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onCancel}
            className="rounded-lg px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-100">
            Cancelar
          </button>
          <button type="button" disabled={!podeConfirmar}
            onClick={() => onConfirm(motivo.trim())}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-40">
            {pending ? "Recusando…" : "Confirmar recusa"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers de avatar ─────────────────────────────────────────────────────────

// Cores estáveis para iniciais (hash do nome → cor)
const AVATAR_COLORS = [
  "#E11D48", "#DB2777", "#9333EA", "#7C3AED", "#4F46E5",
  "#2563EB", "#0891B2", "#059669", "#16A34A", "#CA8A04",
  "#EA580C", "#DC2626",
];
function avatarColor(name: string | null | undefined): string {
  // Defensivo: pauta.recentCommenters em algumas pautas tinha null no meio do
  // array (sem reason óbvia, talvez bug do server). Sem este guard, quebra a
  // página INTEIRA com TypeError: Cannot read properties of null (length).
  const safe = name && typeof name === "string" ? name : "?";
  let h = 0;
  for (let i = 0; i < safe.length; i++) h = (h * 31 + safe.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}
function initials(name: string | null | undefined): string {
  const safe = (name && typeof name === "string" ? name : "?").trim();
  const parts = (safe || "?").split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function Avatar({ name, url, size = 18 }: { name: string; url?: string | null; size?: number }) {
  if (url) {
    return (
      <img
        src={url}
        alt={name}
        title={name}
        className="rounded-full object-cover shrink-0 border border-white"
        style={{ width: size, height: size }}
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
      />
    );
  }
  return (
    <div
      className="rounded-full flex items-center justify-center text-white font-bold shrink-0 border border-white"
      style={{
        width: size, height: size,
        background: avatarColor(name),
        fontSize: Math.max(8, size * 0.42),
      }}
      title={name}
    >
      {initials(name)}
    </div>
  );
}

// ─── Brand tokens ─────────────────────────────────────────────────────────────

type ColDef = { id: Coluna; label: string; color: string };
// Quadro padrão (clientes sem config própria). A lista mora em shared/etapas.ts,
// a mesma que o server e a API do MCP usam. sugestao_tema NÃO é coluna de
// pauta: renderiza temas (status=sugestao) como cards especiais.
export const COLUNAS_DEFAULT: ColDef[] = ETAPAS_PADRAO as ColDef[];

// Coluna virtual do board para card em etapa que o quadro do cliente não tem.
const COLUNA_FORA = "__fora_do_quadro";

// Subset usado em dropdowns que selecionam etapas de PAUTA (filtro, mover pra,
// criar pauta). sugestao_tema fica de fora — é uma etapa de TEMA, não pauta.
const COLUNAS_PAUTA_DEFAULT: ColDef[] =
  COLUNAS_DEFAULT.filter(c => c.id !== "sugestao_tema");

// Config de coluna por cliente (Opção B): o Provider injeta o quadro do cliente
// ativo. Fora do Provider (card aberto pelo Calendário, por exemplo) o hook
// busca o quadro do cliente sozinho. Antes caía no padrão, e o seletor de
// etapa do card mostrava "Roteiro" para a emiteaí: card movido ali sumia do
// quadro (out/26, Agnes).
const ColumnsCtx = React.createContext<ColDef[] | null>(null);
function useColumns(): ColDef[] {
  const ctx = React.useContext(ColumnsCtx);
  const { quadro } = useQuadro();
  return ctx ?? (quadro as ColDef[]);
}

// Jun/26 (Agnes EmiteAí): 3 campos extras nas pautas/temas.
// Opções vieram da planilha (Calendario_Junho_2026_Atualizado).
const PERSONA_OPCOES   = ["Gestor", "Decisor", "Analista", "Compliance"];
const FUNIL_OPCOES: { v: "T" | "M" | "F"; label: string; color: string; bg: string }[] = [
  { v: "T", label: "Topo",  color: "#0A0A0A", bg: "#EDEBE6" },
  { v: "M", label: "Meio",  color: "#7C3AED", bg: "#F3F0FF" },
  { v: "F", label: "Fundo", color: "#EC4899", bg: "#FDE8F4" },
];
// Segmento aceita string livre (varia por cliente) — placeholder mostra exemplos.

const REDE_INFO: Record<Rede, { label: string; bg: string; text: string; icon: string; color: string }> = {
  instagram: { label: "Instagram", bg: "#FDE8F5", text: "#BE185D", icon: "", color: "#E1306C" },
  tiktok:    { label: "TikTok",    bg: "#E6FBF4", text: "#065F46", icon: "", color: "#0A0A0A" },
  youtube:   { label: "YouTube",   bg: "#FEE2E2", text: "#991B1B", icon: "▶️", color: "#FF0000" },
  linkedin:  { label: "LinkedIn",  bg: "#DBEAFE", text: "#1E40AF", icon: "", color: "#0A66C2" },
  twitter:   { label: "Twitter/X", bg: "#E0F2FE", text: "#0C4A6E", icon: "𝕏",  color: "#1DA1F2" },
  multiplas: { label: "Múltiplas", bg: "#F3E8FF", text: "#6B21A8", icon: "", color: "#9333EA" },
};

// Fallback defensivo para redes desconhecidas (legado, texto livre, etc)
const REDE_FALLBACK = { label: "—", bg: "#F3F4F6", text: "#374151", icon: "", color: "#9CA3AF" };
function getRedeInfo(rede: string | null | undefined) {
  if (!rede) return REDE_FALLBACK;
  return (REDE_INFO as Record<string, typeof REDE_FALLBACK>)[rede] ?? REDE_FALLBACK;
}

// Formatos disponíveis por rede (HU-062)
const FORMATOS_POR_REDE: Record<Rede, Formato[]> = {
  instagram: ["feed", "reels", "stories", "carrossel"],
  tiktok:    ["video"],
  youtube:   ["video"],
  linkedin:  ["post", "carrossel", "video"],
  twitter:   ["post", "thread"],
  multiplas: ["feed", "reels", "stories", "carrossel", "video", "post", "thread"],
};

const FORMATO_LABEL: Record<Formato, string> = {
  feed: "Feed", reels: "Reels", stories: "Stories",
  carrossel: "Carrossel", video: "Vídeo", post: "Post", thread: "Thread",
  comentario: "Comentário",
};

const KPI_MACROS = [
  { value: "autoridade",  label: "Autoridade de Marca" },
  { value: "alcance",     label: "Ampliar Alcance" },
  { value: "engajamento", label: "Engajamento Qualificado" },
  { value: "leads",       label: "Geração de Leads" },
  { value: "retencao",    label: "Retenção de Clientes" },
  { value: "fiscal",      label: "Oportunidade Fiscal" },
];

// HU-063: Linhas editoriais com cores visuais
const LINHAS_EDITORIAIS = [
  { value: "L1 — Compliance & Controle",       color: "#0A0A0A", bg: "#EDEBE6" },
  { value: "L2 — Orquestração sem Milagre",    color: "#2563EB", bg: "#DBEAFE" },
  { value: "L3 — Dinheiro na Mesa",            color: "#10B981", bg: "#D1FAE5" },
  { value: "L4 — Bastidores da Operação",      color: "#52525B", bg: "#FEF3C7" },
  { value: "L5 — Maturidade e Visão",          color: "#8B5CF6", bg: "#EDE9FE" },
  { value: "L6 — Tendências e Provocações",    color: "#EC4899", bg: "#FDE8F5" },
];

function linhaColor(val: string | null | undefined): { color: string; bg: string } {
  const found = LINHAS_EDITORIAIS.find(l => val?.startsWith(l.value.split(" ")[0]));
  return found ?? { color: "#262626", bg: "#F5F4F1" };
}

// Taxonomia oficial (slide/PPT). Linha editorial = os 5 PILARES (seletor).
// Território temático = os 6 territórios L1–L6 (LINHAS_EDITORIAIS acima).
const PILARES = [
  "Educação de Mercado",
  "Serviço e Produto",
  "Eficiência e ROI",
  "Branding e Cultura",
  "Mercado e Tendências",
];

// Cor semântica por etapa (id), independente da config de coluna do cliente.
// O rótulo continua vindo do cliente (Opção B); a COR é global e legível pra
// dar leitura de estado sem ler o texto. Verde = só Publicado (no ar); azul =
// Aprovado (pronto); índigo = Agendado. Estados terminais não colidem mais.
export const STATUS_COLOR: Record<string, string> = ETAPA_COR;

function colunaInfo(id: string, cols: ColDef[] = COLUNAS_DEFAULT) {
  const def = cols.find(c => c.id === id) ?? { id, label: `${rotuloEtapa(id, cols)} (fora do quadro)`, color: "#888" };
  return { ...def, color: STATUS_COLOR[id] ?? def.color };
}

// Formata data ISO (YYYY-MM-DD) pra pt-BR (DD/MM/AAAA). Deixa outros formatos
// como estão. Usado na Tabela, que antes imprimia a string ISO crua.
function fmtDataBR(s?: string | null): string {
  if (!s) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
}

// Fecha modal no Esc (a11y — o estudo apontou que faltava em todos os modais).
function useEscapeKey(handler: () => void) {
  const ref = React.useRef(handler);
  ref.current = handler;
  React.useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") ref.current(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, []);
}

const INPUT_CLS = "w-full border border-[#E7E5E0] rounded-lg px-3 py-2.5 text-sm text-[#0A0A0A] placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#D6D3CD] focus:border-[#0A0A0A] transition bg-white";

// ─── LinhaEditorialPicker (HU-063) ───────────────────────────────────────────

// Seletor de opções canônicas. Se o valor atual não estiver na lista (legado),
// ele ainda aparece como opção "(atual)" pra não sumir — mas o padrão é limpo.
function TaxonomiaSelect({
  label, value, options, onChange, placeholder = "— selecione —",
}: {
  label: string; value: string; options: string[];
  onChange: (v: string) => void; placeholder?: string;
}) {
  const v = value ?? "";
  const foraDaLista = v && !options.includes(v);
  return (
    <div>
      <label className="block text-[10px] font-bold text-[#0A0A0A] uppercase tracking-widest mb-1.5">
        {label}
      </label>
      <select
        value={v}
        onChange={e => onChange(e.target.value)}
        className={INPUT_CLS}
      >
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
        {foraDaLista && <option value={v}>{v} (atual)</option>}
      </select>
    </div>
  );
}

// Linha editorial = seletor dos 5 PILARES (limpo, sem texto livre).
function LinhaEditorialPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return <TaxonomiaSelect label="Linha editorial" value={value} options={PILARES} onChange={onChange} />;
}

// Território temático = seletor dos 6 territórios L1–L6.
function TerritorioPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return <TaxonomiaSelect label="Território temático" value={value} options={LINHAS_EDITORIAIS.map(l => l.value)} onChange={onChange} />;
}

// ─── PDF (carrossel do LinkedIn sobe como documento) ─────────────────────────
// PDF não tem miniatura nativa: nas listas vira um bloco com ícone; no destaque
// do card abre no visualizador do navegador, com link para outra aba (no
// celular o navegador às vezes só mostra a primeira página).

export function PdfTile({ nome, compacto = false }: { nome?: string; compacto?: boolean }) {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-[#F3F1EC] text-[#B42318]">
      <svg className={compacto ? "w-5 h-5" : "w-8 h-8"} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 3h7l5 5v12a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M14 3v5h5" />
      </svg>
      <span className="text-[10px] font-bold tracking-wider">PDF</span>
      {!compacto && nome && <span className="text-[10px] text-zinc-500 truncate max-w-[90%] px-1">{nome}</span>}
    </div>
  );
}

function PdfHero({ url, nome, indice, total }: { url: string; nome: string; indice: number; total: number }) {
  return (
    <div className="w-full bg-white flex flex-col" style={{ aspectRatio: "1 / 1" }}>
      <iframe src={url} title={nome || "PDF"} className="w-full flex-1 min-h-0 border-0" />
      <div className="flex items-center justify-between gap-3 px-3 py-2 bg-[#0A0A0A] text-white text-[11px] font-semibold">
        <span className="truncate">PDF{total > 1 ? ` · ${indice + 1}/${total}` : ""} · {nome}</span>
        <a href={url} target="_blank" rel="noopener noreferrer" className="shrink-0 underline hover:text-white/80">Abrir em outra aba</a>
      </div>
    </div>
  );
}

// ─── PautaThumb (esconde o container se a img/vídeo der erro) ────────────────

function PautaThumb({ url, tipo }: { url: string; tipo: string }) {
  const [broken, setBroken] = useState(false);
  if (broken) return null;
  if (tipo === "pdf") {
    return <div className="relative w-full h-32 overflow-hidden"><PdfTile /></div>;
  }
  return (
    <div className="relative w-full h-32 bg-[#EDEBE6] overflow-hidden">
      {tipo === "imagem" ? (
        <img
          src={url}
          alt=""
          loading="eager"
          className="w-full h-full object-cover"
          onError={() => setBroken(true)}
        />
      ) : (
        <>
          <video
            src={url}
            className="w-full h-full object-cover"
            muted
            preload="metadata"
            onError={() => setBroken(true)}
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/15 pointer-events-none">
            <div className="w-9 h-9 rounded-full bg-white/95 flex items-center justify-center">
              <svg className="w-4 h-4 text-[#0A0A0A] ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── PautaCard ────────────────────────────────────────────────────────────────

// ─── Campos extras (Agnes EmiteAí, jun/26) ──────────────────────────────────
// Segmento (texto livre — ex: "TR", "E·T", "M·I"), Persona (4 opções fixas),
// Funil (T/M/F). Render compacto em 3 colunas pra não ocupar muito espaço.
function SegPersonaFunilFields({
  segmento, onSegmento, persona, onPersona, funil, onFunil, layout = "grid",
}: {
  segmento: string; onSegmento: (v: string) => void;
  persona: string;  onPersona:  (v: string) => void;
  funil: "" | "T" | "M" | "F"; onFunil: (v: "" | "T" | "M" | "F") => void;
  layout?: "grid" | "stack";
}) {
  const ipt = "w-full border border-[#E7E5E0] rounded-lg px-3 py-2 text-sm text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#D6D3CD] focus:border-[#0A0A0A] transition bg-white";
  const lbl = "block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5";
  const wrap = layout === "grid" ? "grid grid-cols-3 gap-3" : "space-y-3";
  return (
    <div className={wrap}>
      <div>
        <label className={lbl}>Segmento</label>
        <input type="text" value={segmento} onChange={e => onSegmento(e.target.value)}
          placeholder="ex: TR, E·T, M·I" className={ipt} />
      </div>
      <div>
        <label className={lbl}>Persona</label>
        <select value={persona} onChange={e => onPersona(e.target.value)} className={ipt}>
          <option value="">—</option>
          {PERSONA_OPCOES.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>
      <div>
        <label className={lbl}>Funil</label>
        <div className="flex gap-1">
          {FUNIL_OPCOES.map(f => {
            const active = funil === f.v;
            return (
              <button key={f.v} type="button"
                onClick={() => onFunil(active ? "" : f.v)}
                className="flex-1 px-2 py-2 text-xs font-semibold rounded-lg border-2 transition-all"
                style={{
                  borderColor: active ? f.color : "#E7E5E0",
                  background: active ? f.bg : "white",
                  color: active ? f.color : "#888",
                }}
                title={f.label}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── TemaFormModal — criar/editar tema direto do kanban ─────────────────────
// Substituiu a página /temas (Agnes, jun/26 — "essa tela não pode mais existir").
// Reusa os mesmos endpoints temas.create / temas.update / temas.list (com id).
function TemaFormModal({
  mode, clientId, onClose, onSaved,
}: {
  mode: "new" | { id: number };
  clientId: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editId = typeof mode === "object" ? mode.id : null;
  const { data: temas = [] } = trpc.temas.list.useQuery(
    { clientId }, { enabled: !!editId && !!clientId }
  );
  const tema = editId ? (temas as any[]).find(t => t.id === editId) : null;

  const [titulo, setTitulo]       = useState("");
  const [linhaEdit, setLinhaEdit] = useState("");
  const [territorio, setTerritorio] = useState("");
  const [canalRede, setCanalRede] = useState<"Instagram" | "LinkedIn" | "Múltiplas">("Instagram");
  const [formato, setFormato]     = useState("");
  const [dataPub, setDataPub]     = useState("");
  const [objetivo, setObjetivo]   = useState("");
  const [direcionamento, setDirecionamento] = useState("");
  const [segmento, setSegmento]   = useState("");
  const [persona, setPersona]     = useState("");
  const [funil, setFunil]         = useState<"" | "T" | "M" | "F">("");
  // Out/26 (Agnes, carga de novembro): o tema mostra e edita tudo que a
  // planilha traz, sem campo novo: código, semana, responsável, link,
  // observações e o vínculo com a peça-mãe (porta-voz e ângulo do comentário).
  const [codigo, setCodigo]       = useState("");
  const [semana, setSemana]       = useState("");
  const [responsavel, setResponsavel] = useState("");
  const [linkReferencia, setLinkReferencia] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [parentCodigo, setParentCodigo] = useState("");
  const [portaVoz, setPortaVoz]   = useState("");
  const [anguloComentario, setAnguloComentario] = useState("");
  // A rede só reescreve os canais quando a pessoa troca a rede no seletor;
  // assim "LinkedIn Marca" ou "Instagram Reels" vindos da planilha não viram
  // "LinkedIn"/"Instagram" ao salvar.
  const [redeMexida, setRedeMexida] = useState(false);

  // Carrega valores quando edição
  React.useEffect(() => {
    if (!tema) return;
    setTitulo(tema.tema ?? "");
    setLinhaEdit(tema.linhaEditorial ?? "");
    setTerritorio(tema.territorio ?? "");
    const c = (tema.canais ?? [])[0] ?? "Instagram";
    setCanalRede((c.includes("LinkedIn") && tema.canais.length > 1) ? "Múltiplas" : (c.includes("LinkedIn") ? "LinkedIn" : "Instagram"));
    setFormato(tema.formato ?? "");
    setDataPub(tema.dataPub ?? "");
    setObjetivo(tema.objetivo ?? "");
    setDirecionamento(tema.direcionamento ?? "");
    setSegmento(tema.segmento ?? "");
    setPersona(tema.persona ?? "");
    setFunil((tema.funil ?? "") as any);
    setCodigo(tema.codigo ?? "");
    setSemana(tema.semana ?? "");
    setResponsavel(tema.responsavel ?? "");
    setLinkReferencia(tema.linkReferencia ?? "");
    setObservacoes(tema.observacoes ?? "");
    setParentCodigo(tema.parentCodigo ?? "");
    setPortaVoz(tema.portaVoz ?? "");
    setAnguloComentario(tema.anguloComentario ?? "");
    setRedeMexida(false);
  }, [tema]);

  const temasLista = temas as any[];
  const mae = parentCodigo.trim()
    ? temasLista.find(t => (t.codigo ?? "").trim().toLowerCase() === parentCodigo.trim().toLowerCase()) ?? null
    : null;
  const filhos = codigo.trim()
    ? temasLista.filter(t => t.id !== editId && (t.parentCodigo ?? "").trim().toLowerCase() === codigo.trim().toLowerCase())
    : [];
  const ehComentario = /coment/i.test(formato) || !!portaVoz.trim() || !!anguloComentario.trim();

  const createM = trpc.temas.create.useMutation({ onSuccess: () => { onSaved(); onClose(); } });
  const updateM = trpc.temas.update.useMutation({ onSuccess: () => { onSaved(); onClose(); } });
  const isPending = createM.isPending || updateM.isPending;

  function handleSave() {
    if (!titulo.trim() || isPending) return;
    const canaisDaRede = canalRede === "Múltiplas" ? ["Instagram", "LinkedIn"] : [canalRede];
    if (editId) {
      // Edição: campo apagado grava vazio (antes o valor antigo voltava).
      updateM.mutate({
        id: editId,
        tema: titulo.trim(),
        codigo: codigo.trim(),
        linhaEditorial: linhaEdit.trim(),
        territorio: territorio.trim(),
        ...(redeMexida ? { canais: canaisDaRede } : {}),
        formato: formato.trim(),
        dataPub: dataPub,
        semana: semana.trim(),
        objetivo: objetivo.trim(),
        direcionamento: direcionamento.trim(),
        segmento: segmento.trim(),
        persona: persona,
        funil: (funil || null) as "T" | "M" | "F" | null,
        responsavel: responsavel.trim(),
        linkReferencia: linkReferencia.trim(),
        observacoes: observacoes.trim(),
        parentCodigo: parentCodigo.trim() || null,
        portaVoz: portaVoz.trim() || null,
        anguloComentario: anguloComentario.trim() || null,
      } as any);
    } else {
      createM.mutate({
        clientId,
        codigo: codigo.trim(),
        tema: titulo.trim(),
        linhaEditorial: linhaEdit.trim() || undefined,
        territorio: territorio.trim() || undefined,
        canais: canaisDaRede,
        formato: formato.trim() || undefined,
        dataPub: dataPub || undefined,
        semana: semana.trim() || undefined,
        objetivo: objetivo.trim() || undefined,
        direcionamento: direcionamento.trim() || undefined,
        segmento: segmento.trim() || undefined,
        persona: persona || undefined,
        funil: (funil || undefined) as "T" | "M" | "F" | undefined,
        responsavel: responsavel.trim() || undefined,
        linkReferencia: linkReferencia.trim() || undefined,
        observacoes: observacoes.trim() || undefined,
        parentCodigo: parentCodigo.trim() || null,
        portaVoz: portaVoz.trim() || null,
        anguloComentario: anguloComentario.trim() || null,
      } as any);
    }
  }

  const INPUT = "w-full border border-[#E7E5E0] rounded-lg px-3 py-2 text-sm text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#D6D3CD] focus:border-[#0A0A0A] transition bg-white";
  const LABEL = "block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5";

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-x-4 top-16 bottom-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-lg md:max-h-[90vh] z-50 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ border: "1px solid #E7E5E0" }}>
        <div className="flex items-center justify-between px-6 py-4 shrink-0" style={{ borderBottom: "1px solid #E7E5E0" }}>
          <h2 className="font-bold text-base text-[#0A0A0A]" style={{ fontFamily: "Poppins, sans-serif" }}>
            {editId ? "Editar tema" : "Novo tema"}
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div>
            <label className={LABEL}>Título *</label>
            <input autoFocus type="text" value={titulo} onChange={e => setTitulo(e.target.value)}
              placeholder="Sobre o que é o post?" className={INPUT} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Código</label>
              <input type="text" value={codigo} onChange={e => setCodigo(e.target.value)} maxLength={20}
                placeholder="NV01" className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Semana</label>
              <input type="text" value={semana} onChange={e => setSemana(e.target.value)} maxLength={30}
                placeholder="S1 · 05 e 06/11" className={INPUT} />
            </div>
          </div>
          <LinhaEditorialPicker value={linhaEdit} onChange={setLinhaEdit} />
          <TerritorioPicker value={territorio} onChange={setTerritorio} />
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={LABEL}>Rede</label>
              <select value={canalRede} onChange={e => { setCanalRede(e.target.value as any); setRedeMexida(true); }} className={INPUT}>
                <option value="Instagram">Instagram</option>
                <option value="LinkedIn">LinkedIn</option>
                <option value="Múltiplas">Múltiplas</option>
              </select>
            </div>
            <div>
              <label className={LABEL}>Formato</label>
              <select value={formato} onChange={e => setFormato(e.target.value)} className={INPUT}>
                <option value="">—</option>
                <option value="post">Post</option>
                <option value="carrossel">Carrossel</option>
                <option value="reels">Reels</option>
                <option value="stories">Stories</option>
                <option value="Comentário de porta-voz">Comentário de porta-voz</option>
                {formato && !["post", "carrossel", "reels", "stories", "Comentário de porta-voz"].includes(formato) && (
                  <option value={formato}>{formato} (atual)</option>
                )}
              </select>
            </div>
            <div>
              <label className={LABEL}>Data</label>
              <input type="date" value={dataPub} onChange={e => setDataPub(e.target.value)} className={INPUT} />
            </div>
          </div>
          {editId && tema && (tema.canais ?? []).length > 0 && !redeMexida && (
            <p className="text-[11px] text-zinc-500 -mt-2">Canal: {(tema.canais as string[]).join(", ")}</p>
          )}
          <SegPersonaFunilFields
            segmento={segmento} onSegmento={setSegmento}
            persona={persona} onPersona={setPersona}
            funil={funil} onFunil={setFunil}
          />
          <div>
            <label className={LABEL}>Responsável</label>
            <input type="text" value={responsavel} onChange={e => setResponsavel(e.target.value)} maxLength={200}
              placeholder="Quem conduz a peça" className={INPUT} />
          </div>

          {/* Peças relacionadas: vínculo com a mãe (comentário de porta-voz, story) */}
          <div className="rounded-lg p-3 space-y-3" style={{ border: "1px solid #E7E5E0", background: "#FAFAF8" }}>
            <p className="text-[11px] font-bold uppercase tracking-widest text-[var(--brand)]">Peças relacionadas</p>
            <div>
              <label className={LABEL}>Peça-mãe (código)</label>
              <input type="text" value={parentCodigo} onChange={e => setParentCodigo(e.target.value)} maxLength={20}
                placeholder="Vazio se esta é a peça principal" className={INPUT} />
              {parentCodigo.trim() && (
                <p className="text-[11px] mt-1" style={{ color: mae ? "#46443E" : "#B42318" }}>
                  {mae ? `Peça de ${mae.codigo}: ${mae.tema}` : "Nenhum tema com esse código. Ao aprovar, liga com a pauta que tiver esse código."}
                </p>
              )}
            </div>
            {ehComentario && (
              <>
                <div>
                  <label className={LABEL}>Porta-voz</label>
                  <input type="text" value={portaVoz} onChange={e => setPortaVoz(e.target.value)} maxLength={200}
                    placeholder="Quem assina o comentário" className={INPUT} />
                </div>
                <div>
                  <label className={LABEL}>Ângulo do comentário</label>
                  <textarea value={anguloComentario} onChange={e => setAnguloComentario(e.target.value)}
                    rows={2} className={INPUT} placeholder="O que o comentário traz que não está na peça" />
                </div>
              </>
            )}
            {filhos.length > 0 && (
              <div className="space-y-1">
                <p className="text-[11px] text-zinc-500">Ligadas a esta peça:</p>
                {filhos.map((f: any) => (
                  <p key={f.id} className="text-xs text-[#0A0A0A]">
                    <span className="font-semibold">{f.codigo}</span> · {f.portaVoz ? `Comentário de ${f.portaVoz}` : (f.formato ?? "Peça")}
                  </p>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className={LABEL}>Objetivo</label>
            <textarea value={objetivo} onChange={e => setObjetivo(e.target.value)}
              rows={2} className={INPUT} placeholder="O que esse post quer alcançar?" />
          </div>
          <div>
            <label className={LABEL}>Direcionamento</label>
            <textarea value={direcionamento} onChange={e => setDirecionamento(e.target.value)}
              rows={3} className={INPUT} placeholder="Como deve ser construído?" />
          </div>
          <div>
            <label className={LABEL}>Observações</label>
            <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)}
              rows={observacoes.split("\n").length > 4 ? 10 : 3} className={INPUT} placeholder="Notas de produção, insumo, prazo, destino" />
          </div>
          <div>
            <label className={LABEL}>Link de referência</label>
            <input type="url" value={linkReferencia} onChange={e => setLinkReferencia(e.target.value)}
              placeholder="https://" className={INPUT} />
          </div>
          {(createM.error || updateM.error) && (
            <p className="text-red-600 text-xs">{(createM.error || updateM.error)!.message}</p>
          )}
        </div>
        <div className="flex gap-3 px-6 py-4 shrink-0" style={{ borderTop: "1px solid #E7E5E0" }}>
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-[#E7E5E0] text-[#0A0A0A] text-sm font-semibold hover:bg-[#F5F4F1]">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={!titulo.trim() || isPending}
            className="flex-1 py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-40"
            style={{ background: "linear-gradient(90deg, #0A0A0A, #262626)", fontFamily: "Poppins, sans-serif" }}>
            {isPending ? "Salvando…" : (editId ? "Salvar" : "Criar tema")}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Card de Tema (renderiza na coluna "Sugestão de Tema") ───────────────────
// Sugestão da Vanessa (EmiteAí, jun/26): aprovar tema direto no kanban em vez
// de pular pra /temas. Card minimalista com Aprovar (vira pauta na coluna
// "Ideação") / Descartar (soft-delete).
function TemaCard({
  tema, onAprovar, onDescartar, onClick, filhos = 0,
}: {
  tema: {
    id: number; tema: string; linhaEditorial: string | null; territorio?: string | null;
    canais: string[]; formato: string | null;
    segmento?: string | null; persona?: string | null; funil?: "T" | "M" | "F" | null;
    codigo?: string | null; dataPub?: string | null;
    parentCodigo?: string | null; portaVoz?: string | null;
  };
  onAprovar: (id: number) => void;
  onDescartar: (id: number) => void;
  onClick: (temaId: number) => void;
  filhos?: number;
}) {
  const funilInfo = tema.funil ? FUNIL_OPCOES.find(f => f.v === tema.funil) : null;
  // Rótulo da etapa onde a pauta nasce, do quadro do cliente (antes era fixo "Roteiros").
  const quadro = useColumns();
  const destino = rotuloEtapa(etapaInicial(quadro), quadro);
  const dataBR = tema.dataPub && /^\d{4}-\d{2}-\d{2}/.test(tema.dataPub)
    ? `${tema.dataPub.slice(8, 10)}/${tema.dataPub.slice(5, 7)}` : null;
  return (
    <div
      onClick={() => onClick(tema.id)}
      className="bg-white rounded-xl border p-3 cursor-pointer transition-all shadow-[var(--elev-1)] hover:shadow-[var(--elev-2)] hover:-translate-y-0.5"
      style={{ borderColor: "var(--solar-line)", borderLeft: "3px solid #9333EA" }}
    >
      {(tema.codigo || dataBR) && (
        <div className="flex items-center gap-1.5 mb-1.5 text-[10px] font-semibold text-zinc-500">
          {tema.codigo && <span className="px-1.5 py-0.5 rounded bg-[#F3F1EC] text-[#46443E]">{tema.codigo}</span>}
          {dataBR && <span>{dataBR}</span>}
        </div>
      )}
      <p
        className="text-[#0A0A0A] text-sm font-semibold leading-snug mb-2 line-clamp-3"
        style={{ fontFamily: "Poppins, sans-serif" }}
      >
        {tema.tema}
      </p>
      {/* Território temático (Agnes jul/26) */}
      {tema.territorio && (
        <p className="text-[10px] text-zinc-400 font-medium mb-1.5 line-clamp-1">{tema.territorio}</p>
      )}
      {(tema.linhaEditorial || tema.formato) && (
        <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
          {tema.linhaEditorial && (
            <span className="text-[10px] text-[#7C3AED] font-semibold px-2 py-0.5 rounded-full bg-[#F3F0FF]">{tema.linhaEditorial}</span>
          )}
          {tema.formato && (
            <span className="text-[10px] text-[#46443E] font-medium px-2 py-0.5 rounded-full bg-[#F3F1EC]">{tema.formato.charAt(0).toUpperCase() + tema.formato.slice(1)}</span>
          )}
        </div>
      )}
      {(tema.persona || tema.segmento || funilInfo || tema.canais.length > 0) && (
        <p className="text-[10px] text-zinc-400 mb-2 truncate">
          {[funilInfo?.label, tema.persona, tema.segmento, ...tema.canais.slice(0, 2)].filter(Boolean).join("  ·  ")}
        </p>
      )}
      {(tema.parentCodigo || filhos > 0) && (
        <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
          {tema.parentCodigo && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full truncate max-w-full" style={{ background: "var(--brand-soft)", color: "var(--brand-ink)" }}
              title={`Peça relacionada a ${tema.parentCodigo}`}>
              ↳ {tema.parentCodigo}{tema.portaVoz ? ` · ${tema.portaVoz}` : ""}
            </span>
          )}
          {filhos > 0 && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#F3F1EC", color: "#46443E" }}
              title="Peças relacionadas a este tema">
              🔗 {filhos} {filhos === 1 ? "peça" : "peças"}
            </span>
          )}
        </div>
      )}
      <div className="flex gap-1.5 mt-2 pt-2 border-t border-[#F5F4F1]">
        <button
          onClick={e => {
            e.stopPropagation();
            // Confirmação (Agnes/Bela jul/26): aprovar move o card pra produção
            // e não dá pra desfazer com 1 clique — evita aprovação acidental.
            if (confirm(`Aprovar este tema? Ele sai da Sugestão e vira uma pauta em ${destino}.`)) {
              onAprovar(tema.id);
            }
          }}
          className="flex-1 text-[11px] font-semibold py-1.5 rounded-md transition-colors"
          style={{ background: "#0A0A0A", color: "white", fontFamily: "Poppins, sans-serif" }}
          title={`Aprovar: cria pauta em ${destino}`}
        >
          ✓ Aprovar
        </button>
        <button
          onClick={e => { e.stopPropagation(); onDescartar(tema.id); }}
          className="px-2 text-[11px] font-semibold py-1.5 rounded-md transition-colors border border-[#E7E5E0] text-zinc-500 hover:bg-[#FEE2E2] hover:text-red-600 hover:border-red-200"
          title="Recusar tema (pede o motivo)"
        >
          🚫
        </button>
      </div>
    </div>
  );
}

function PautaCard({
  pauta,
  thumb,
  onMove,
  onDelete,
  onClick,
  onVoltarSugestao,
  selecionavel,
  selecionado,
  onToggleSelecao,
  childCount = 0,
  onDuplicar,
}: {
  pauta: Pauta;
  thumb?: { url: string; tipo: string };
  onMove: (id: number, coluna: ColunaPauta) => void;
  onDelete: (id: number) => void;
  onClick: (pauta: Pauta) => void;
  onVoltarSugestao?: (id: number) => void;
  selecionavel?: boolean;
  selecionado?: boolean;
  onToggleSelecao?: (id: number) => void;
  childCount?: number;
  onDuplicar?: (pauta: Pauta) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const COLUNAS_PAUTA = useColumns().filter(c => c.id !== "sugestao_tema") as { id: ColunaPauta; label: string; color: string }[];
  const rede = getRedeInfo(pauta.rede);
  // Só considera colunas de pauta (exclui sugestao_tema — pauta nunca está lá)
  const colunaIdx = COLUNAS_PAUTA.findIndex(c => c.id === pauta.coluna);
  // Fora do quadro (colunaIdx -1): sem setas, escolhe a etapa pelo menu.
  const prevCol = colunaIdx > 0 ? COLUNAS_PAUTA[colunaIdx - 1] : null;
  const nextCol = colunaIdx >= 0 && colunaIdx < COLUNAS_PAUTA.length - 1 ? COLUNAS_PAUTA[colunaIdx + 1] : null;
  const lc = linhaColor(pauta.linhaEditorial);
  // Tag "ClickUp" no card: SÓ Raz (@razconsulting.com.br) enxerga, e só quando o card tem link da task.
  const { session: cardSession } = useAuth();
  const razInterno = (cardSession?.email ?? "").toLowerCase().trim().endsWith("@razconsulting.com.br");

  return (
    <div
      className="bg-white rounded-xl border transition-all group relative cursor-pointer overflow-hidden shadow-[var(--elev-1)] hover:shadow-[var(--elev-2)] hover:-translate-y-0.5"
      style={{
        borderColor: selecionado ? "var(--brand)" : "var(--solar-line)",
        borderLeft: `3px solid ${STATUS_COLOR[pauta.coluna] ?? "#E7E3DA"}`,
        boxShadow: selecionado ? "0 0 0 2px var(--brand-soft)" : undefined,
      }}
      onClick={() => { if (selecionavel) onToggleSelecao?.(pauta.id); else onClick(pauta); }}
    >
      {/* Thumbnail da primeira mídia */}
      {thumb && <PautaThumb url={thumb.url} tipo={thumb.tipo} />}

      <div className="p-3.5">
      <div className="flex items-start justify-between gap-2 mb-2">
        {selecionavel && (
          <span
            className="w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 mt-0.5"
            style={{
              borderColor: selecionado ? "#0A0A0A" : "#D6D3CD",
              background: selecionado ? "#0A0A0A" : "white",
            }}
          >
            {selecionado && (
              <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </span>
        )}
        <p className="text-[#0A0A0A] text-sm font-semibold leading-snug flex-1 line-clamp-2" style={{ fontFamily: "Poppins, sans-serif" }}>
          {pauta.titulo}
        </p>
        <div className={cn("relative shrink-0", selecionavel && "hidden")} onClick={e => e.stopPropagation()}>
          <button
            onClick={() => setMenuOpen(o => !o)}
            className="w-6 h-6 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#F5F4F1]"
            style={{ color: "#0A0A0A" }}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
            </svg>
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-7 z-20 bg-white rounded-xl border border-[#E7E5E0] shadow-lg py-1 w-44 text-xs">
                {prevCol && (
                  <button
                    onClick={() => { onMove(pauta.id, prevCol.id); setMenuOpen(false); }}
                    className="w-full text-left px-3 py-2 hover:bg-[#F5F4F1] text-[#0A0A0A] font-medium"
                  >
                    ← {prevCol.label}
                  </button>
                )}
                {nextCol && (
                  <button
                    onClick={() => { onMove(pauta.id, nextCol.id); setMenuOpen(false); }}
                    className="w-full text-left px-3 py-2 hover:bg-[#F5F4F1] text-[#0A0A0A] font-medium"
                  >
                    → {nextCol.label}
                  </button>
                )}
                {(prevCol || nextCol) && <div className="border-t border-[#E7E5E0] my-1" />}
                {onVoltarSugestao && (
                  <button
                    onClick={() => {
                      if (confirm("Voltar este card pra 'Sugestão de Tema'? A pauta atual será removida (nenhum trabalho de arte/legenda é perdido — vira tema de novo).")) {
                        onVoltarSugestao(pauta.id);
                      }
                      setMenuOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-[#F3F0FF] text-[#7C3AED] font-medium"
                  >
                    ↩ Voltar pra Sugestão
                  </button>
                )}
                {onDuplicar && (
                  <button
                    onClick={() => { onDuplicar(pauta); setMenuOpen(false); }}
                    className="w-full text-left px-3 py-2 hover:bg-[#F5F4F1] text-[#0A0A0A] font-medium"
                  >
                    ⧉ Duplicar…
                  </button>
                )}
                {pauta.coluna !== "desaprovado" && (
                  <button
                    onClick={() => { onMove(pauta.id, "desaprovado"); setMenuOpen(false); }}
                    className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-600 font-medium"
                  >
                    🚫 Recusar (com motivo)
                  </button>
                )}
                <button
                  onClick={() => { onDelete(pauta.id); setMenuOpen(false); }}
                  className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-500 font-medium"
                >
                  Mover p/ Lixeira
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Comentários: marca compacta (era uma caixa âmbar grande) */}
      {(pauta.commentCount ?? 0) > 0 && (
        <div
          className="inline-flex items-center gap-1.5 mb-2 text-[11px] font-semibold"
          style={{ color: "#B45309" }}
          title={pauta.recentCommenters && pauta.recentCommenters.length > 0 ? `Comentaram: ${pauta.recentCommenters.join(", ")}` : ""}
        >
          <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 24 24">
            <path d="M21 6h-2v9H6v2c0 .55.45 1 1 1h11l4 4V7c0-.55-.45-1-1-1zm-4 6V3c0-.55-.45-1-1-1H3c-.55 0-1 .45-1 1v14l4-4h10c.55 0 1-.45 1-1z" />
          </svg>
          <span>{pauta.commentCount} {(pauta.commentCount ?? 0) === 1 ? "comentário" : "comentários"}</span>
          {pauta.lastCommentBy && <span className="text-zinc-400 font-normal">· {pauta.lastCommentBy}</span>}
        </div>
      )}

      {pauta.linhaEditorial && (
        <span
          className="inline-flex items-center gap-1 text-[11px] font-semibold mb-2 px-2 py-0.5 rounded-full"
          style={{ background: lc.bg, color: lc.color }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: lc.color }} />
          {pauta.linhaEditorial}
        </span>
      )}

      {/* Interno Raz: tag quando o card tem link da task no ClickUp (abre a task sem abrir o modal) */}
      {razInterno && pauta.linkClickup && (
        <a
          href={pauta.linkClickup}
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          title="Abrir a task no ClickUp (visível só para a Raz)"
          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide mb-2 ml-1.5 px-2 py-0.5 rounded-full hover:brightness-95"
          style={{ background: "#EDEBFF", color: "#4C3AA8", border: "1px solid #C7C3F5" }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#7B68EE" }} />
          ClickUp
        </a>
      )}

      {/* Essenciais: rede + formato. O resto da taxonomia vira uma linha
          discreta em vez de um amontoado de 6 pills (estudo de UX). */}
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        <span
          className="inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full"
          style={{ background: rede.bg, color: rede.text, fontFamily: "Poppins, sans-serif" }}
        >
          {rede.icon} {rede.label}
        </span>
        <span className="inline-flex text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#F3F1EC] text-[#46443E]">
          {FORMATO_LABEL[pauta.formato]}
        </span>
        {/* Peça atrelada a um post-mãe (story de engajamento / comentário de porta-voz) */}
        {pauta.parentId != null && (
          <span
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full"
            style={{ background: "var(--brand-soft)", color: "var(--brand-ink)" }}
            title="Peça atrelada a um post"
          >
            ↳ {pauta.formato === "comentario" ? "porta-voz" : "peça"}
          </span>
        )}
        {/* Post-mãe com peças relacionadas anexadas */}
        {pauta.parentId == null && childCount > 0 && (
          <span
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full"
            style={{ background: "var(--brand-soft)", color: "var(--brand-ink)" }}
            title={`${childCount} peça(s) relacionada(s)`}
          >
            🔗 {childCount} peça{childCount > 1 ? "s" : ""}
          </span>
        )}
      </div>
      {(pauta.funil || pauta.persona || pauta.segmento) && (
        <p className="text-[11px] text-zinc-400 mb-2 truncate">
          {[
            pauta.funil ? (FUNIL_OPCOES.find(x => x.v === pauta.funil)?.label ?? null) : null,
            pauta.persona || null,
            pauta.segmento || null,
          ].filter(Boolean).join("  ·  ")}
        </p>
      )}

      <div className="flex items-center justify-between mt-1">
        <div className="flex gap-1 flex-wrap"></div>
        <div className="flex items-center gap-2">
          {pauta.responsavel && (
            <span
              className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
              style={{ background: "linear-gradient(135deg, #71717A, #0A0A0A)" }}
            >
              {pauta.responsavel.charAt(0).toUpperCase()}
            </span>
          )}
          {pauta.dataPrevista && (
            <span className="text-[10px] text-zinc-400 font-medium">
              {new Date(pauta.dataPrevista + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}
            </span>
          )}
        </div>
      </div>
      </div> {/* end .p-3.5 */}
    </div>
  );
}

// ─── ComentariosSection (HU-054) ─────────────────────────────────────────────

function ComentariosSection({ pautaId, clientId }: { pautaId: number; clientId: number }) {
  const [novoTexto, setNovoTexto] = useState("");
  const { data: comentarios = [], refetch } = trpc.comentarios.list.useQuery({ pautaId });
  const createMutation = trpc.comentarios.create.useMutation({ onSuccess: () => { refetch(); setNovoTexto(""); } });
  const deleteMutation = trpc.comentarios.delete.useMutation({ onSuccess: () => refetch() });

  function handleSend() {
    const t = novoTexto.trim();
    if (!t || createMutation.isPending) return;
    createMutation.mutate({ pautaId, clientId, texto: t });
  }

  return (
    <div style={{ borderTop: "1px solid #E7E5E0" }} className="pt-4">
      <label className="block text-[10px] font-bold text-[#0A0A0A] uppercase tracking-widest mb-3">
        Comentários ({comentarios.length})
      </label>

      {/* Lista de comentários */}
      {comentarios.length > 0 && (
        <div className="space-y-3 mb-4">
          {comentarios.map(c => (
            <div key={c.id} className="flex gap-2.5 group">
              <span
                className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0 mt-0.5"
                style={{ background: "linear-gradient(135deg,#0A0A0A,#71717A)" }}
              >
                {c.autor.charAt(0).toUpperCase()}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-semibold text-[#0A0A0A]">{c.autor}</span>
                  <span className="text-[11px] text-zinc-400">
                    {new Date(c.createdAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                <p className="text-sm text-zinc-600 leading-relaxed whitespace-pre-wrap">{c.texto}</p>
              </div>
              <button
                onClick={() => deleteMutation.mutate({ id: c.id })}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-300 hover:text-red-400 shrink-0 self-start mt-1"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input novo comentário */}
      <div className="flex gap-2">
        <textarea
          value={novoTexto}
          onChange={e => setNovoTexto(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder="Escreva um comentário… (Enter para enviar)"
          rows={2}
          className={INPUT_CLS + " resize-none flex-1 text-sm"}
        />
        <button
          onClick={handleSend}
          disabled={!novoTexto.trim() || createMutation.isPending}
          className="px-3 py-2 rounded-lg text-white text-xs font-semibold disabled:opacity-40 transition-all shrink-0 self-end"
          style={{ background: "linear-gradient(135deg,#0A0A0A,#262626)" }}
        >
          Enviar
        </button>
      </div>
    </div>
  );
}

// ─── AtividadesSection ───────────────────────────────────────────────────────
// Log de movimentações do card: quem moveu de qual coluna pra qual e quando.
// Alimentado por pautas.move / pautas.update no backend.

function AtividadesSection({ pautaId }: { pautaId: number }) {
  const COLUNAS = useColumns();
  const { data: atividades = [] } = trpc.pautas.atividades.useQuery({ pautaId });
  const [aberto, setAberto] = useState(false);
  if (atividades.length === 0) return null;
  const label = (c: string | null) => COLUNAS.find(x => x.id === c)?.label ?? c ?? "—";

  return (
    <div style={{ borderTop: "1px solid #E7E5E0" }} className="pt-4">
      <button onClick={() => setAberto(o => !o)} className="w-full flex items-center gap-2 mb-3 text-left">
        <span className="text-[10px] font-bold text-[#0A0A0A] uppercase tracking-widest">Histórico de movimentações ({atividades.length})</span>
        <svg className={cn("w-3.5 h-3.5 text-[var(--solar-mute)] transition-transform ml-auto", aberto && "rotate-90")} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
      </button>
      {aberto && (
      <div className="space-y-2.5">
        {atividades.map(a => (
          <div key={a.id} className="flex gap-2.5 items-start">
            <span
              className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0 mt-0.5"
              style={{ background: "linear-gradient(135deg,#0A0A0A,#71717A)" }}
            >
              {(a.autorNome ?? "?").charAt(0).toUpperCase()}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-zinc-600 leading-relaxed">
                <span className="font-semibold text-[#0A0A0A]">{a.autorNome ?? "Alguém"}</span>
                {" moveu de "}
                <span className="font-medium text-zinc-700">{label(a.deColuna)}</span>
                {" → "}
                <span className="font-medium text-zinc-700">{label(a.paraColuna)}</span>
              </p>
              <span className="text-[11px] text-zinc-400">
                {new Date(a.createdAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}

// ─── PecasRelacionadasSection ────────────────────────────────────────────────
// Peças atreladas a um post-mãe: stories de engajamento e comentários de
// porta-voz. Pedido Raz/EmiteAí (set/26): a enquete/story e o comentário de
// porta-voz nascem colados ao post que engajam, viram card-filho (parentId),
// entram no fluxo de aprovação normal. Hierarquia de 1 nível só: o post-mãe
// mostra os botões de criar peça; a peça-filho só mostra o vínculo com a mãe.

const PECA_TIPOS: { formato: Formato; label: string; hint: string }[] = [
  { formato: "stories",    label: "Story de engajamento", hint: "Enquete/caixinha que puxa o alcance do post" },
  { formato: "comentario", label: "Comentário de porta-voz", hint: "Comentário assinado por um porta-voz p/ alcançar a rede dele" },
];

function PecasRelacionadasSection({
  pauta, clientId, onOpenRelated, onChanged,
}: {
  pauta: Pauta;
  clientId: number;
  onOpenRelated: (p: Pauta) => void;
  onChanged: () => void;
}) {
  const COLUNAS = useColumns();
  const { data: todas = [], refetch } = trpc.pautas.list.useQuery({ clientId });
  const lista = todas as Pauta[];
  const parent = pauta.parentId != null ? lista.find(p => p.id === pauta.parentId) ?? null : null;
  const filhos = lista.filter(p => p.parentId === pauta.id);
  const isChild = pauta.parentId != null;

  const createMutation = trpc.pautas.create.useMutation({
    onSuccess: (novo: any) => {
      refetch(); onChanged();
      if (novo && typeof novo === "object" && novo.id) onOpenRelated(novo as Pauta);
    },
  });

  function addPeca(formato: Formato, labelPrefixo: string) {
    if (createMutation.isPending) return;
    createMutation.mutate({
      clientId,
      parentId: pauta.id,
      titulo: `${labelPrefixo}: ${pauta.titulo}`.slice(0, 250),
      rede: pauta.rede as any,
      formato: formato as any,
      coluna: etapaInicial(COLUNAS) as any,
      linhaEditorial: pauta.linhaEditorial ?? undefined,
    } as any);
  }

  // Nada a mostrar: peça-filho sem mãe carregada, ou post sem filhos — mas
  // no post sempre mostramos os botões de adicionar, então só escondemos
  // quando é filho e a mãe não veio na lista (raro).
  if (isChild && !parent && filhos.length === 0) return null;

  return (
    <div style={{ borderTop: "1px solid #E7E5E0" }} className="pt-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[11px] font-bold uppercase tracking-widest text-[var(--brand)]" style={{ fontFamily: "Poppins, sans-serif" }}>
          Peças relacionadas
        </span>
      </div>

      {/* Vínculo com o post-mãe (quando esta pauta é uma peça-filho) */}
      {isChild && parent && (
        <button
          onClick={() => onOpenRelated(parent)}
          className="w-full flex items-center gap-2 mb-3 p-2.5 rounded-lg text-left transition-colors hover:bg-[var(--brand-soft)]"
          style={{ border: "1px solid #E7E5E0" }}
        >
          <svg className="w-4 h-4 text-[var(--brand)] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 17l-5-5m0 0l5-5m-5 5h12" />
          </svg>
          <span className="text-[11px] text-zinc-500 shrink-0">Peça de</span>
          <span className="text-xs font-semibold text-[#0A0A0A] truncate">{parent.titulo}</span>
          <span className="ml-auto text-[10px] font-medium text-zinc-400 shrink-0">{FORMATO_LABEL[parent.formato] ?? parent.formato}</span>
        </button>
      )}

      {/* Lista de peças-filho (no post-mãe) */}
      {filhos.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {filhos.map(f => (
            <button
              key={f.id}
              onClick={() => onOpenRelated(f)}
              className="w-full flex items-center gap-2.5 p-2.5 rounded-lg text-left transition-colors hover:bg-[var(--brand-soft)]"
              style={{ border: "1px solid #E7E5E0" }}
            >
              <span
                className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold"
                style={{ background: "var(--brand-soft)", color: "var(--brand-ink)" }}
              >
                {f.formato === "comentario" ? "Comentário" : f.formato === "stories" ? "Story" : (FORMATO_LABEL[f.formato] ?? f.formato)}
              </span>
              <div className="flex-1 min-w-0">
                <span className="block text-xs font-medium text-[#0A0A0A] truncate">{f.titulo}</span>
                {f.formato === "comentario" && f.portaVoz && (
                  <span className="block text-[11px] text-zinc-500 truncate">Porta-voz: {f.portaVoz}</span>
                )}
              </div>
              <span className="ml-auto shrink-0 w-2 h-2 rounded-full" style={{ background: colunaInfo(f.coluna, COLUNAS).color }} title={colunaInfo(f.coluna, COLUNAS).label} />
            </button>
          ))}
        </div>
      )}

      {/* Ângulo + porta-voz editáveis (quando esta pauta É um comentário de porta-voz) */}
      {isChild && pauta.formato === "comentario" && (
        <PortaVozFields pauta={pauta} onChanged={() => { refetch(); onChanged(); }} />
      )}

      {/* Botões de adicionar peça — só no post-mãe (hierarquia de 1 nível) */}
      {!isChild && (
        <div className="flex flex-wrap gap-2">
          {PECA_TIPOS.map(t => (
            <button
              key={t.formato}
              onClick={() => addPeca(t.formato, t.label)}
              disabled={createMutation.isPending}
              title={t.hint}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
              style={{ border: "1px solid var(--brand)", color: "var(--brand-ink)", background: "var(--brand-soft)" }}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Campos do comentário de porta-voz: quem assina + o ângulo/gancho do comentário.
// Salva na hora (update) — separado do form principal p/ não depender do modo edição.
function PortaVozFields({ pauta, onChanged }: { pauta: Pauta; onChanged: () => void }) {
  const [portaVoz, setPortaVoz] = useState(pauta.portaVoz ?? "");
  const [angulo, setAngulo] = useState(pauta.anguloComentario ?? "");
  const [salvo, setSalvo] = useState(false);
  const updateMutation = trpc.pautas.update.useMutation({
    onSuccess: () => { setSalvo(true); setTimeout(() => setSalvo(false), 1800); onChanged(); },
  });
  const dirty = (portaVoz !== (pauta.portaVoz ?? "")) || (angulo !== (pauta.anguloComentario ?? ""));
  function salvar() {
    if (updateMutation.isPending) return;
    updateMutation.mutate({ id: pauta.id, portaVoz: portaVoz.trim() || null, anguloComentario: angulo.trim() || null } as any);
  }
  return (
    <div className="mb-3 p-3 rounded-lg space-y-2.5" style={{ background: "var(--brand-soft)", border: "1px solid #E7E5E0" }}>
      <div>
        <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-1">Porta-voz</label>
        <input
          value={portaVoz}
          onChange={e => setPortaVoz(e.target.value)}
          placeholder="Nome de quem vai assinar o comentário"
          className="w-full px-2.5 py-1.5 rounded-md text-xs bg-white"
          style={{ border: "1px solid #E7E5E0" }}
        />
      </div>
      <div>
        <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-1">Ângulo do comentário</label>
        <textarea
          value={angulo}
          onChange={e => setAngulo(e.target.value)}
          placeholder="O que o porta-voz vai comentar (gancho p/ alcançar a rede dele)"
          rows={2}
          className="w-full px-2.5 py-1.5 rounded-md text-xs bg-white resize-none"
          style={{ border: "1px solid #E7E5E0" }}
        />
      </div>
      {dirty && (
        <button
          onClick={salvar}
          disabled={updateMutation.isPending}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-opacity disabled:opacity-50"
          style={{ background: "var(--brand)" }}
        >
          {updateMutation.isPending ? "Salvando…" : "Salvar porta-voz"}
        </button>
      )}
      {salvo && !dirty && <span className="text-[11px] font-medium text-emerald-600">Salvo ✓</span>}
    </div>
  );
}

// ─── MidiasSection (HU-051/052) ──────────────────────────────────────────────
// mode="hero":     peça em destaque (carrossel grande no topo do detalhe)
// mode="compact":  grid 3-col tradicional (anexo)

function MidiasSection({ pautaId, clientId, mode = "compact" }: { pautaId: number; clientId: number; mode?: "hero" | "compact" }) {
  const [uploading, setUploading] = useState(false);
  const [erro, setErro] = useState("");
  const [fileEl, setFileEl] = useState<HTMLInputElement | null>(null);
  const [replacingId, setReplacingId] = useState<number | null>(null);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [heroIndex, setHeroIndex] = useState(0);

  const { data: midias = [], refetch } = trpc.midias.list.useQuery({ pautaId });
  const createMutation = trpc.midias.create.useMutation();
  const deleteMutation = trpc.midias.delete.useMutation({ onSuccess: () => refetch() });

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    const oldId = replacingId;
    setReplacingId(null);
    if (!file) return;
    e.target.value = ""; // permite reenviar o mesmo arquivo
    setErro("");

    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const isVid = file.type.startsWith("video/") || ["mp4", "mov", "m4v", "webm", "avi", "mkv", "qt", "3gp", "ogv"].includes(ext);
    const isImg = file.type.startsWith("image/") || ["gif", "png", "jpg", "jpeg", "webp", "heic", "heif", "bmp", "svg", "avif", "tiff"].includes(ext);
    const isPdf = file.type === "application/pdf" || ext === "pdf";
    if (!isImg && !isVid && !isPdf) { setErro("Envie uma imagem, um vídeo ou um PDF."); return; }
    if (isImg && file.size > 50 * 1024 * 1024) { setErro("Imagem excede 50MB."); return; }
    if (isVid && file.size > 500 * 1024 * 1024) { setErro("Vídeo excede 500MB."); return; }
    if (isPdf && file.size > 100 * 1024 * 1024) { setErro("PDF excede 100MB."); return; }

    setUploading(true);
    try {
      const raw = localStorage.getItem(CLIENT_AUTH_KEY);
      const token = raw ? (JSON.parse(raw).token ?? "") : "";
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/upload/media", {
        method: "POST",
        headers: { "x-client-token": token },
        body: fd,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error ?? "Falha no upload");
      }
      const data = await res.json();
      await createMutation.mutateAsync({
        pautaId, clientId,
        url: data.url, nome: data.nome, tipo: data.tipo, tamanho: data.tamanho,
      });
      // HU-052: substituir → remove a mídia antiga após subir a nova
      if (oldId != null) await deleteMutation.mutateAsync({ id: oldId });
      refetch();
    } catch (e: any) {
      setErro(e.message ?? "Erro ao enviar arquivo");
    } finally {
      setUploading(false);
    }
  }

  function fmtTamanho(bytes: number) {
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  function pickFile(replaceId?: number) {
    setReplacingId(replaceId ?? null);
    fileEl?.click();
  }

  const viewing = viewerIndex != null ? midias[viewerIndex] : null;

  // ─── MODE: HERO ───────────────────────────────────────────────────────────
  // Peça em destaque para AVALIAÇÃO: imagem grande, carrossel se múltiplas,
  // botões flutuantes pra substituir/adicionar/remover.
  if (mode === "hero") {
    const safeIdx = Math.min(heroIndex, Math.max(0, midias.length - 1));
    const current = midias[safeIdx];

    // Empty state: usa fundo bege da app (claro), não preto — preto só faz sentido
    // quando tem peça real pra exibir (viewer estilo Instagram).
    const containerCls = midias.length === 0 ? "relative bg-[#F5F4F1]" : "relative bg-[#0A0A0A]";

    return (
      <div className={containerCls}>
        <input
          ref={setFileEl}
          type="file"
          accept="image/*,video/*,application/pdf,.mp4,.mov,.gif,.webm,.m4v,.pdf"
          className="hidden"
          onChange={handleFile}
        />

        {midias.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-10 text-center border-y border-dashed border-[#E7E5E0]">
            <div className="w-12 h-12 rounded-full bg-white border border-[#E7E5E0] flex items-center justify-center mb-3 shadow-sm">
              <svg className="w-6 h-6 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-zinc-600 text-sm mb-3">Nenhuma peça anexada ainda</p>
            <button
              onClick={() => pickFile()}
              disabled={uploading}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#0A0A0A] text-white hover:bg-[#262626] disabled:opacity-50"
            >
              {uploading ? "Enviando…" : "+ Adicionar peça"}
            </button>
            <p className="text-[10px] text-zinc-400 mt-3">Imagens até 50MB · Vídeos até 500MB · PDF até 100MB</p>
            {erro && <p className="text-xs text-red-600 mt-2">{erro}</p>}
          </div>
        ) : (
          <>
            {/* Viewer da peça (clicável para abrir lightbox). PDF não cabe
                dentro de botão: os cliques precisam chegar no visualizador. */}
            {current.tipo === "pdf" ? (
              <PdfHero url={current.url} nome={current.nome} indice={safeIdx} total={midias.length} />
            ) : (
            <button
              onClick={() => setViewerIndex(safeIdx)}
              className="block w-full bg-[#0A0A0A] group relative"
              title="Ampliar"
              style={{ aspectRatio: "1 / 1" }}
            >
              {current.tipo === "imagem" ? (
                <img
                  src={current.url}
                  alt={current.nome}
                  className="w-full h-full object-contain"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = "0.3"; }}
                />
              ) : (
                <video
                  src={current.url}
                  className="w-full h-full object-contain"
                  controls
                  onClick={e => e.stopPropagation()}
                />
              )}

              {/* Badge contador e tipo */}
              <div className="absolute top-3 left-3 flex items-center gap-2">
                {midias.length > 1 && (
                  <span className="bg-black/70 text-white text-[11px] px-2 py-1 rounded-full font-semibold">
                    {safeIdx + 1} / {midias.length}
                  </span>
                )}
                {current.tipo === "video" && (
                  <span className="bg-black/70 text-white text-[11px] px-2 py-1 rounded-full font-semibold">
                    ▶ vídeo
                  </span>
                )}
              </div>

              {/* Hint ampliar */}
              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="bg-black/70 text-white text-[11px] px-2 py-1 rounded-full font-semibold flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                  Ampliar
                </span>
              </div>
            </button>
            )}

            {/* Setas de navegação */}
            {midias.length > 1 && (
              <>
                <button
                  onClick={() => setHeroIndex(i => (i - 1 + midias.length) % midias.length)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => setHeroIndex(i => (i + 1) % midias.length)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}

            {/* Toolbar de ações (flutua na parte de baixo) */}
            <div className="flex items-center justify-between gap-2 px-3 py-2.5 bg-black/95">
              <span className="text-white/70 text-[11px] truncate flex-1" title={current.nome}>
                {current.nome} · {fmtTamanho(current.tamanho)}
              </span>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  onClick={() => pickFile(current.id)}
                  disabled={uploading}
                  className="px-2.5 py-1.5 rounded-md text-[11px] font-semibold bg-white/10 text-white hover:bg-white/20 disabled:opacity-40 flex items-center gap-1"
                  title="Substituir esta peça"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Substituir
                </button>
                <button
                  onClick={() => pickFile()}
                  disabled={uploading}
                  className="px-2.5 py-1.5 rounded-md text-[11px] font-semibold bg-white text-[#0A0A0A] hover:bg-white/90 disabled:opacity-40 flex items-center gap-1"
                >
                  {uploading ? (
                    <>
                      <span className="w-3 h-3 rounded-full border-2 border-[#D6D3CD] border-t-[#0A0A0A] animate-spin" />
                      …
                    </>
                  ) : (
                    <>+ Adicionar</>
                  )}
                </button>
                <button
                  onClick={() => { if (confirm("Remover esta peça?")) { deleteMutation.mutate({ id: current.id }); setHeroIndex(0); } }}
                  className="px-2 py-1.5 rounded-md text-[11px] font-semibold bg-red-500/20 text-red-300 hover:bg-red-500 hover:text-white flex items-center gap-1"
                  title="Remover"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Thumbs strip (só quando tem múltiplas) */}
            {midias.length > 1 && (
              <div className="flex gap-1.5 px-3 py-2 bg-[#0A0A0A] overflow-x-auto">
                {midias.map((m, i) => (
                  <button
                    key={m.id}
                    onClick={() => setHeroIndex(i)}
                    className="shrink-0 rounded overflow-hidden border-2 transition-all"
                    style={{ borderColor: i === safeIdx ? "white" : "transparent", opacity: i === safeIdx ? 1 : 0.55 }}
                  >
                    {m.tipo === "imagem" ? (
                      <img src={m.url} alt={m.nome} className="w-14 h-14 object-cover" />
                    ) : m.tipo === "pdf" ? (
                      <div className="w-14 h-14"><PdfTile compacto /></div>
                    ) : (
                      <div className="w-14 h-14 bg-zinc-800 flex items-center justify-center text-white/70 text-xs">▶</div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {erro && <p className="text-xs text-red-400 px-3 py-2 bg-black">{erro}</p>}
          </>
        )}

        {/* lightbox em zoom (compartilhado entre hero e compact) */}
        {viewing && (
          <div
            className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center"
            onClick={() => setViewerIndex(null)}
          >
            <button
              onClick={() => setViewerIndex(null)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            {midias.length > 1 && (
              <button
                onClick={e => { e.stopPropagation(); setViewerIndex(i => (i! - 1 + midias.length) % midias.length); }}
                className="absolute left-4 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <div className="max-w-[90vw] max-h-[85vh] flex flex-col items-center gap-3" onClick={e => e.stopPropagation()}>
              {viewing.tipo === "imagem" ? (
                <img src={viewing.url} alt={viewing.nome} className="max-w-[90vw] max-h-[78vh] object-contain rounded-lg" />
              ) : viewing.tipo === "pdf" ? (
                <iframe src={viewing.url} title={viewing.nome} className="w-[90vw] max-w-4xl h-[78vh] rounded-lg bg-white border-0" />
              ) : (
                <video src={viewing.url} className="max-w-[90vw] max-h-[78vh] rounded-lg" controls autoPlay />
              )}
              <p className="text-white/80 text-sm">
                {viewing.nome} · {fmtTamanho(viewing.tamanho)}
                <span className="text-white/40 ml-2">{(viewerIndex ?? 0) + 1}/{midias.length}</span>
              </p>
            </div>
            {midias.length > 1 && (
              <button
                onClick={e => { e.stopPropagation(); setViewerIndex(i => (i! + 1) % midias.length); }}
                className="absolute right-4 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // ─── MODE: COMPACT (anexo legado) ─────────────────────────────────────────
  return (
    <div style={{ borderTop: "1px solid #E7E5E0" }} className="pt-4">
      <div className="flex items-center justify-between mb-3">
        <label className="text-[10px] font-bold text-[#0A0A0A] uppercase tracking-widest">
          Mídias ({midias.length})
        </label>
        <button
          onClick={() => pickFile()}
          disabled={uploading}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all disabled:opacity-50 hover:bg-[#EDEBE6]"
          style={{ borderColor: "#0A0A0A", color: "#0A0A0A" }}
        >
          {uploading ? (
            <>
              <span className="w-3 h-3 rounded-full border-2 border-[#D6D3CD] border-t-[#0A0A0A] animate-spin" />
              Enviando…
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Adicionar
            </>
          )}
        </button>
        <input
          ref={setFileEl}
          type="file"
          accept="image/*,video/*,application/pdf,.mp4,.mov,.gif,.webm,.m4v,.pdf"
          className="hidden"
          onChange={handleFile}
        />
      </div>

      <p className="text-[11px] text-zinc-400 mb-3">Imagens até 50MB · Vídeos até 500MB · PDF até 100MB</p>

      {erro && <p className="text-xs text-red-500 mb-2">{erro}</p>}

      {midias.length > 0 ? (
        <div className="grid grid-cols-3 gap-2">
          {midias.map((m, idx) => (
            <div key={m.id} className="relative group rounded-lg overflow-hidden border border-[#E7E5E0] bg-[#FAFAFE]">
              <button
                onClick={() => setViewerIndex(idx)}
                className="block w-full"
                title="Ampliar"
              >
                {m.tipo === "imagem" ? (
                  <img src={m.url} alt={m.nome} className="w-full h-24 object-cover" />
                ) : m.tipo === "pdf" ? (
                  <div className="w-full h-24"><PdfTile nome={m.nome} /></div>
                ) : (
                  <video src={m.url} className="w-full h-24 object-cover" controls={false} />
                )}
              </button>
              {m.tipo === "video" && (
                <span className="absolute top-1 left-1 bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded font-semibold pointer-events-none">
                  ▶ vídeo
                </span>
              )}
              <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => pickFile(m.id)}
                  className="w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-[#0A0A0A]"
                  title="Substituir"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                <button
                  onClick={() => { if (confirm("Remover esta mídia?")) deleteMutation.mutate({ id: m.id }); }}
                  className="w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-red-500"
                  title="Remover"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <span className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[9px] px-1.5 py-0.5 truncate opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                {m.nome} · {fmtTamanho(m.tamanho)}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-zinc-400 italic">Nenhuma mídia anexada ainda.</p>
      )}

      {/* HU-052: visualizador (lightbox) com setas */}
      {viewing && (
        <div
          className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center"
          onClick={() => setViewerIndex(null)}
        >
          <button
            onClick={() => setViewerIndex(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {midias.length > 1 && (
            <button
              onClick={e => { e.stopPropagation(); setViewerIndex(i => (i! - 1 + midias.length) % midias.length); }}
              className="absolute left-4 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          <div className="max-w-[80vw] max-h-[80vh] flex flex-col items-center gap-3" onClick={e => e.stopPropagation()}>
            {viewing.tipo === "imagem" ? (
              <img src={viewing.url} alt={viewing.nome} className="max-w-[80vw] max-h-[72vh] object-contain rounded-lg" />
            ) : viewing.tipo === "pdf" ? (
              <iframe src={viewing.url} title={viewing.nome} className="w-[80vw] max-w-4xl h-[72vh] rounded-lg bg-white border-0" />
            ) : (
              <video src={viewing.url} className="max-w-[80vw] max-h-[72vh] rounded-lg" controls autoPlay />
            )}
            <p className="text-white/80 text-sm">
              {viewing.nome} · {fmtTamanho(viewing.tamanho)}
              <span className="text-white/40 ml-2">{viewerIndex! + 1}/{midias.length}</span>
            </p>
          </div>

          {midias.length > 1 && (
            <button
              onClick={e => { e.stopPropagation(); setViewerIndex(i => (i! + 1) % midias.length); }}
              className="absolute right-4 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── PautaDetailModal ─────────────────────────────────────────────────────────

// ─── DuplicarModal ───────────────────────────────────────────────────────────
// Duplica o card levando só o que a pessoa marcar. Vem marcado tudo que tem
// valor, menos o que é específico do original (data, horário, etapa, links e
// vínculo de peça relacionada). Comentários e histórico nunca vão.

type CampoDup =
  | "descricao" | "objetivo" | "direcionamento" | "legenda" | "observacoes" | "midias"
  | "rede" | "formato" | "linhaEditorial" | "territorio" | "segmento" | "persona" | "funil"
  | "dataPrevista" | "horarioPostagem" | "responsavel" | "coluna" | "links" | "pecaRelacionada";

const CAMPOS_DUPLICAR: { grupo: string; itens: { key: CampoDup; label: string; razOnly?: boolean }[] }[] = [
  { grupo: "Conteúdo", itens: [
    { key: "descricao", label: "Descrição / briefing" },
    { key: "objetivo", label: "Objetivo" },
    { key: "direcionamento", label: "Direcionamento" },
    { key: "legenda", label: "Legenda" },
    { key: "observacoes", label: "Observações" },
    { key: "midias", label: "Mídias (as mesmas peças)" },
  ]},
  { grupo: "Classificação", itens: [
    { key: "rede", label: "Rede" },
    { key: "formato", label: "Formato" },
    { key: "linhaEditorial", label: "Linha editorial" },
    { key: "territorio", label: "Território" },
    { key: "segmento", label: "Segmento" },
    { key: "persona", label: "Persona" },
    { key: "funil", label: "Funil" },
  ]},
  { grupo: "Planejamento", itens: [
    { key: "dataPrevista", label: "Data prevista" },
    { key: "horarioPostagem", label: "Horário de postagem" },
    { key: "responsavel", label: "Responsável" },
    { key: "coluna", label: "Etapa atual" },
  ]},
  { grupo: "Específicos do original", itens: [
    { key: "links", label: "Links do Drive e do ClickUp", razOnly: true },
    { key: "pecaRelacionada", label: "Vínculo com o post e porta-voz" },
  ]},
];
const DUP_DESMARCADOS = new Set<CampoDup>(["dataPrevista", "horarioPostagem", "coluna", "links", "pecaRelacionada"]);

function DuplicarModal({ pauta, onCancel, onDone }: { pauta: Pauta; onCancel: () => void; onDone: (novo: Pauta) => void }) {
  const COLUNAS = useColumns();
  const { session } = useAuth();
  const isRaz = (session?.email ?? "").toLowerCase().trim().endsWith("@razconsulting.com.br");
  const { data: midias = [] } = trpc.midias.list.useQuery({ pautaId: pauta.id });
  const nMidias = (midias as unknown[]).length;
  const etapaInicial = colunaInfo("ideia", COLUNAS).label;

  function valor(key: CampoDup): string | null {
    const p = pauta;
    switch (key) {
      case "midias": return nMidias > 0 ? `${nMidias} ${nMidias === 1 ? "peça" : "peças"}` : null;
      case "rede": return p.rede ? getRedeInfo(p.rede).label : null;
      case "formato": return p.formato ? (FORMATO_LABEL[p.formato] ?? p.formato) : null;
      case "funil": return p.funil ? (FUNIL_OPCOES.find(x => x.v === p.funil)?.label ?? p.funil) : null;
      case "dataPrevista": return p.dataPrevista ? fmtDataBR(p.dataPrevista) : null;
      case "coluna": return colunaInfo(p.coluna, COLUNAS).label;
      case "links": return [p.linkDrive ? "Drive" : "", p.linkClickup ? "ClickUp" : ""].filter(Boolean).join(" e ") || null;
      case "pecaRelacionada": return p.parentId != null ? `Peça de outro post${p.portaVoz ? ` · porta-voz ${p.portaVoz}` : ""}` : null;
      default: {
        const v = (p as any)[key];
        return v == null || String(v).trim() === "" ? null : String(v);
      }
    }
  }
  const visiveis = CAMPOS_DUPLICAR.map(g => ({ ...g, itens: g.itens.filter(i => !i.razOnly || isRaz) }));
  const comValor = visiveis.flatMap(g => g.itens).filter(i => valor(i.key) != null).map(i => i.key);

  const [titulo, setTitulo] = useState(`${pauta.titulo} (cópia)`.slice(0, 255));
  const [marcados, setMarcados] = useState<Set<CampoDup>>(() => new Set(comValor.filter(k => !DUP_DESMARCADOS.has(k))));
  // as mídias chegam depois do primeiro render: marca quando aparecerem
  const midiasMarcadas = React.useRef(false);
  useEffect(() => {
    if (nMidias > 0 && !midiasMarcadas.current) {
      midiasMarcadas.current = true;
      setMarcados(s => new Set(s).add("midias"));
    }
  }, [nMidias]);

  const dup = trpc.pautas.duplicate.useMutation({ onSuccess: novo => onDone(novo as Pauta) });

  // Esc fecha só esta janela, não o card que está por baixo
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onCancel(); } };
    window.addEventListener("keydown", h, true);
    return () => window.removeEventListener("keydown", h, true);
  }, [onCancel]);

  function alterna(k: CampoDup) {
    setMarcados(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  }
  function confirmar() {
    if (!titulo.trim() || dup.isPending) return;
    dup.mutate({ id: pauta.id, titulo: titulo.trim(), campos: Array.from(marcados).filter(k => comValor.includes(k)) });
  }

  // Portal no body: o painel do card usa translate + overflow hidden, o que
  // prenderia o "fixed" dentro dele e cortaria a janela.
  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] flex flex-col shadow-[var(--elev-3)]" onClick={e => e.stopPropagation()}>
        <div className="px-5 pt-5 pb-3 border-b border-[#E7E5E0]">
          <h3 className="text-base font-semibold text-[#0A0A0A]" style={{ fontFamily: "Poppins, sans-serif" }}>Duplicar card</h3>
          <p className="text-xs text-zinc-500 mt-1">Escolha o que vai para o card novo.</p>
          <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-widest mt-4 mb-1">Título do card novo</label>
          <input value={titulo} onChange={e => setTitulo(e.target.value)} maxLength={255} className={INPUT_CLS} autoFocus />
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          <div className="flex gap-3 mb-2 text-xs">
            <button type="button" onClick={() => setMarcados(new Set(comValor))} className="font-semibold text-[var(--brand)] hover:underline">Marcar tudo</button>
            <button type="button" onClick={() => setMarcados(new Set())} className="font-semibold text-zinc-500 hover:underline">Desmarcar tudo</button>
          </div>
          {visiveis.map(g => (
            <div key={g.grupo} className="mb-3">
              <p className="text-[10px] font-bold text-[var(--brand)] uppercase tracking-widest mb-1.5">{g.grupo}</p>
              {g.itens.map(i => {
                const v = valor(i.key);
                const vazio = v == null;
                return (
                  <label key={i.key} className={cn("flex items-start gap-2.5 py-1.5", vazio ? "opacity-40 cursor-not-allowed" : "cursor-pointer")}>
                    <input
                      type="checkbox"
                      className="mt-0.5 accent-[var(--brand)]"
                      disabled={vazio}
                      checked={!vazio && marcados.has(i.key)}
                      onChange={() => alterna(i.key)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-[#0A0A0A]">{i.label}</span>
                      <span className="block text-[11px] text-zinc-500 truncate">{vazio ? "vazio no original" : v}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          ))}
        </div>

        <div className="px-5 py-3 border-t border-[#E7E5E0]">
          <p className="text-[11px] text-zinc-500 mb-3">
            Comentários e histórico não vão para a cópia, e o card novo ganha ID próprio.
            {!marcados.has("coluna") && <> Ele começa em <strong>{etapaInicial}</strong>.</>}
          </p>
          {dup.error && <p className="text-xs text-red-600 mb-2">{dup.error.message}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-semibold text-zinc-600 hover:bg-[#F5F4F1]">Cancelar</button>
            <button
              type="button"
              onClick={confirmar}
              disabled={!titulo.trim() || dup.isPending}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: "var(--brand)" }}
            >
              {dup.isPending ? "Duplicando…" : "Duplicar card"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// O card usa o quadro do cliente DONO da pauta. Fora do kanban (Calendário em
// "Todos os clientes", deep-link) não há Provider certo; aqui ele é montado com
// o client_id da própria pauta, e as seções internas (etapa, histórico, peças
// relacionadas, duplicar) leem esse quadro.
export function PautaDetailModal(props: React.ComponentProps<typeof PautaDetailModalInner>) {
  const ctx = React.useContext(ColumnsCtx);
  const { session } = useAuth();
  const ativo = session?.activeClientId ?? null;
  const pautaClient = (props.pauta as any)?.clientId ?? null;
  // Provider da página só vale se for do mesmo cliente da pauta.
  const usarCtx = !!ctx && (pautaClient == null || ativo == null || Number(ativo) === Number(pautaClient));
  const { quadro } = useQuadro(pautaClient);
  return (
    <ColumnsCtx.Provider value={usarCtx ? ctx : (quadro as ColDef[])}>
      <PautaDetailModalInner {...props} />
    </ColumnsCtx.Provider>
  );
}

function PautaDetailModalInner({
  pauta: initial,
  onClose,
  onSave,
  onDelete,
  onHardDelete,
  onVoltarSugestao,
  onOpenRelated,
}: {
  pauta: Pauta;
  onClose: () => void;
  onSave: () => void;
  onDelete: (id: number) => void;
  onHardDelete?: (id: number) => void;
  onVoltarSugestao?: (id: number) => void;
  onOpenRelated?: (p: Pauta) => void;
}) {
  const [editing, setEditing] = useState(false);
  const COLUNAS = useColumns();
  const COLUNAS_PAUTA = COLUNAS.filter(c => c.id !== "sugestao_tema") as { id: ColunaPauta; label: string; color: string }[];
  const [form, setForm] = useState<Partial<Pauta>>(initial);
  const [location, navigate] = useLocation(); // HU-055
  // Confirmação inline (não usa window.confirm — que alguns navegadores bloqueiam)
  const [confirmAcao, setConfirmAcao] = useState<null | "arquivar" | "excluir" | "voltar">(null);
  // ID único + links internos: SÓ Raz (@razconsulting.com.br) vê.
  const { session: authSession } = useAuth();
  const isRazEmail = (authSession?.email ?? "").toLowerCase().trim().endsWith("@razconsulting.com.br");

  const colunaIdx = COLUNAS_PAUTA.findIndex(c => c.id === (form.coluna ?? initial.coluna));
  // Fora do quadro (colunaIdx -1): sem setas, escolhe a etapa pelo menu.
  const prevCol = colunaIdx > 0 ? COLUNAS_PAUTA[colunaIdx - 1] : null;
  const nextCol = colunaIdx >= 0 && colunaIdx < COLUNAS_PAUTA.length - 1 ? COLUNAS_PAUTA[colunaIdx + 1] : null;
  const currentColInfo = colunaInfo(form.coluna ?? initial.coluna, COLUNAS);
  const [stageMenuOpen, setStageMenuOpen] = useState(false);

  const updateMutation = trpc.pautas.update.useMutation({
    onSuccess: () => { onSave(); setEditing(false); },
  });
  const moveMutation = trpc.pautas.move.useMutation({ onSuccess: onSave });
  const desaprovarMutation = trpc.pautas.desaprovar.useMutation({
    onSuccess: () => { setDesaprovarOpen(false); onSave(); },
  });
  const [desaprovarOpen, setDesaprovarOpen] = useState(false);

  // Download das peças do card (vídeo/poster) — pra pegar os assets aprovados.
  const { data: midiasDoCard = [] } = trpc.midias.list.useQuery({ pautaId: initial.id });
  function baixarMidias() {
    const ms = midiasDoCard as { url: string; nome: string }[];
    if (!ms.length) return;
    ms.forEach((m, i) => setTimeout(() => {
      const a = document.createElement("a");
      a.href = m.url; a.download = m.nome || `peca-${i + 1}`;
      document.body.appendChild(a); a.click(); a.remove();
    }, i * 400));
  }
  const [linkCopiado, setLinkCopiado] = useState(false);
  const [duplicarOpen, setDuplicarOpen] = useState(false);
  function copiarLink() {
    const key = initial.contentId || String(initial.id);
    const url = `${window.location.origin}/c/${key}`;
    navigator.clipboard?.writeText(url).then(() => {
      setLinkCopiado(true); setTimeout(() => setLinkCopiado(false), 2000);
    }).catch(() => {});
  }

  // QA gate — interno Raz (@razconsulting.com.br). Cliente nunca vê.
  const QA_ON = initial.clientId === 1 && isRazEmail;
  const [qaOpen, setQaOpen] = useState(false);
  const [qaData, setQaData] = useState<any | null>((initial as any).qaResult ?? null);
  const [qaChecking, setQaChecking] = useState(false);
  const qaCheck = trpc.qa.check.useMutation();
  const qaResolve = trpc.qa.resolveItem.useMutation();
  const qaPend = ((qaData?.issues ?? []) as any[]).filter(i => !i.resolvido).length;
  async function runQa() {
    if (qaChecking) return;
    setQaChecking(true); setQaOpen(true);
    try {
      const texto = String(form.legenda ?? initial.legenda ?? "").trim();
      const res = await qaCheck.mutateAsync({ texto, clienteId: initial.clientId, contexto: "legenda + arte", pautaId: initial.id, comImagem: true });
      setQaData(res);
    } catch { /* não trava */ }
    setQaChecking(false);
  }
  async function toggleQaItem(itemId: number, resolvido: boolean) {
    setQaData((d: any) => d ? { ...d, issues: d.issues.map((i: any) => i.id === itemId ? { ...i, resolvido } : i) } : d);
    try { await qaResolve.mutateAsync({ pautaId: initial.id, itemId, resolvido }); } catch {}
  }

  function set<K extends keyof Pauta>(key: K, val: Pauta[K]) {
    setForm(f => ({ ...f, [key]: val }));
    if (!editing) setEditing(true);
  }

  function handleSave() { doSave(); }

  function doSave() {
    if (updateMutation.isPending) return;
    updateMutation.mutate({
      id: initial.id,
      titulo:         form.titulo,
      descricao:      form.descricao ?? null,
      rede:           form.rede as any,
      formato:        form.formato,
      coluna:         form.coluna as any,
      responsavel:    form.responsavel ?? null,
      dataPrevista:   form.dataPrevista ?? null,
      tags:           form.tags,
      linhaEditorial: form.linhaEditorial ?? null,
      territorio:     (form as any).territorio ?? null,
      objetivo:       (form as any).objetivo ?? null,
      direcionamento: (form as any).direcionamento ?? null,
      segmento:       form.segmento ?? null,
      persona:        form.persona ?? null,
      funil:          (form.funil as any) ?? null,
      kpiMacro:       form.kpiMacro ?? null,
      gancho:         form.gancho ?? null,
      prazoEntrega:   form.prazoEntrega ?? null,
      perfilAlvo:     form.perfilAlvo ?? null,
      legenda:        form.legenda ?? null,
      linkDrive:      (form as any).linkDrive ?? null,
      linkClickup:    (form as any).linkClickup ?? null,
      horarioPostagem: (form as any).horarioPostagem ?? null,
      observacoes:    (form as any).observacoes ?? null,
      // Porta-voz e ângulo têm salvamento próprio (PortaVozFields). Mandar aqui
      // o valor de quando o card abriu desfazia o que foi salvo ali (out/26).
    } as any);
  }

  function handleMove(coluna: ColunaPauta) {
    if (coluna === "desaprovado") { setDesaprovarOpen(true); return; } // pop-up de motivo
    // Se o servidor recusar a etapa, a tela volta para a anterior (senão o
    // próximo Salvar mandava a etapa recusada e falhava inteiro).
    const anterior = (form.coluna ?? initial.coluna) as ColunaPauta;
    moveMutation.mutate({ id: initial.id, coluna }, {
      onError: (e: any) => { setForm(f => ({ ...f, coluna: anterior })); alert(e?.message ?? "Não foi possível mudar a etapa."); },
    });
    setForm(f => ({ ...f, coluna }));
  }

  function handleDiscard() {
    setForm(initial);
    setEditing(false);
  }

  // Guarda de alterações não salvas antes de fechar (estudo: perda de dado silenciosa).
  function requestClose() {
    if (editing && !window.confirm("Há alterações não salvas neste card. Descartar e fechar?")) return;
    onClose();
  }
  useEscapeKey(requestClose);

  const rede = getRedeInfo(form.rede ?? initial.rede);

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm" onClick={requestClose} />

      {/* Popup centralizado (Agnes jul/26: era drawer lateral, virou modal no meio) */}
      <div className="fixed inset-x-4 top-8 bottom-8 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-3xl md:max-h-[90vh] z-50 bg-white rounded-xl flex flex-col overflow-hidden shadow-[var(--elev-3)]" style={{ border: "1px solid var(--solar-line)" }}>

        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 shrink-0" style={{ borderBottom: "1px solid #E7E5E0" }}>
          <button onClick={requestClose} className="text-zinc-400 hover:text-zinc-600 transition-colors shrink-0">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="flex-1 min-w-0">
            <input
              className="w-full font-bold text-[#0A0A0A] text-base bg-transparent border-b-2 border-transparent focus:border-[#0A0A0A] focus:outline-none pb-0.5 transition-colors"
              style={{ fontFamily: "Poppins, sans-serif" }}
              value={form.titulo ?? initial.titulo}
              onChange={e => set("titulo", e.target.value)}
            />
          </div>
          {editing ? (
            <div className="flex gap-2 shrink-0">
              <button
                onClick={handleDiscard}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-[#E7E5E0] text-zinc-500 hover:bg-[#F5F4F1]"
              >
                Descartar
              </button>
              <button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg text-white disabled:opacity-50"
                style={{ background: "linear-gradient(90deg,#0A0A0A,#262626)" }}
              >
                {updateMutation.isPending ? "Salvando…" : "Salvar"}
              </button>
            </div>
          ) : null}
        </div>

        {/* Motivo da recusa — fica no card quando Desaprovado (pedido Sophia jul/26) */}
        {initial.coluna === "desaprovado" && initial.motivoRecusa && (
          <div className="px-5 py-2.5 shrink-0 flex items-start gap-2 text-xs" style={{ borderBottom: "1px solid #FCA5A5", background: "#FEF2F2" }}>
            <span className="shrink-0">🚫</span>
            <div>
              <span className="font-semibold text-red-700">Recusado: </span>
              <span className="text-red-700">{initial.motivoRecusa}</span>
            </div>
          </div>
        )}

        {/* Stage nav */}
        <div className="px-5 py-3 flex items-center gap-2 shrink-0" style={{ borderBottom: "1px solid #E7E5E0", background: "#FAFAFE" }}>
          <button
            onClick={() => prevCol && handleMove(prevCol.id)}
            disabled={!prevCol}
            title={prevCol ? `Voltar para ${prevCol.label}` : ""}
            className="flex items-center px-2 py-1.5 rounded-lg border transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            style={{ borderColor: "#E7E5E0", color: "#262626" }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="flex-1 flex justify-center relative">
            <button
              onClick={() => setStageMenuOpen(o => !o)}
              title="Mudar etapa"
              className="px-3 py-1 text-xs font-bold rounded-full text-white flex items-center gap-1.5"
              style={{ background: currentColInfo.color }}
            >
              {currentColInfo.label}
              <svg className="w-3 h-3 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
            </button>
            {stageMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setStageMenuOpen(false)} />
                <div className="absolute top-8 z-20 bg-white rounded-xl border border-[var(--solar-line)] shadow-[var(--elev-3)] py-1 w-56 max-h-72 overflow-y-auto">
                  {COLUNAS_PAUTA.map(c => {
                    const isCur = (form.coluna ?? initial.coluna) === c.id;
                    return (
                      <button key={c.id} onClick={() => { setStageMenuOpen(false); if (!isCur) handleMove(c.id); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-[var(--sidebar-accent)]"
                        style={{ background: isCur ? "var(--brand-soft)" : undefined }}>
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: STATUS_COLOR[c.id] ?? c.color }} />
                        <span className="flex-1 text-[var(--solar-ink)] font-medium">{c.label}</span>
                        {isCur && <svg className="w-3.5 h-3.5 text-[var(--brand)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <button
            onClick={() => nextCol && handleMove(nextCol.id)}
            disabled={!nextCol}
            title={nextCol ? `Avançar para ${nextCol.label}` : ""}
            className="flex items-center px-2 py-1.5 rounded-lg border transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            style={{ borderColor: "#E7E5E0", color: "#262626" }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {(form.coluna ?? initial.coluna) !== "desaprovado" && (
            <button
              onClick={() => handleMove("desaprovado")}
              title="Recusar este conteúdo (pede o motivo)"
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
            >
              🚫 Recusar
            </button>
          )}
          {midiasDoCard.length > 0 && (
            <button
              onClick={baixarMidias}
              title="Baixar as peças deste card (vídeo/imagem)"
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-colors"
              style={{ borderColor: "#E7E5E0", color: "#262626" }}
            >
              ⬇ Baixar ({midiasDoCard.length})
            </button>
          )}
          <button
            onClick={copiarLink}
            title="Copiar link compartilhável deste card"
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-colors"
            style={{ borderColor: linkCopiado ? "#059669" : "#E7E5E0", color: linkCopiado ? "#059669" : "#262626" }}
          >
            {linkCopiado ? "✓ Copiado" : "🔗 Copiar link"}
          </button>
          <button
            onClick={() => setDuplicarOpen(true)}
            disabled={editing}
            title={editing ? "Salve as alterações antes de duplicar" : "Criar um card novo a partir deste, escolhendo o que levar"}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-colors hover:bg-[#F5F4F1] disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ borderColor: "#E7E5E0", color: "#262626" }}
          >
            ⧉ Duplicar
          </button>
        </div>
        {duplicarOpen && (
          <DuplicarModal
            pauta={initial}
            onCancel={() => setDuplicarOpen(false)}
            onDone={novo => { setDuplicarOpen(false); onSave(); onOpenRelated?.(novo); }}
          />
        )}

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">

          {/* HERO: peça em destaque para avaliação (HU-051/052) */}
          <MidiasSection pautaId={initial.id} clientId={initial.clientId} mode="hero" />

          <div className="px-5 py-5 space-y-4">

          <div className="flex items-center gap-3">
            <span className="text-[11px] font-bold uppercase tracking-widest text-[var(--brand)]" style={{ fontFamily: "Poppins, sans-serif" }}>Classificação</span>
            <div className="flex-1 h-px" style={{ background: "var(--solar-line)" }} />
          </div>

          {/* Rede + Formato */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-[#0A0A0A] uppercase tracking-widest mb-1.5">Rede</label>
              <select
                value={form.rede ?? initial.rede}
                onChange={e => set("rede", e.target.value as Rede)}
                className={INPUT_CLS}
              >
                {(Object.keys(REDE_INFO) as Rede[]).map(r => (
                  <option key={r} value={r}>{getRedeInfo(r).label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-[#0A0A0A] uppercase tracking-widest mb-1.5">Formato</label>
              <select
                value={form.formato ?? initial.formato}
                onChange={e => set("formato", e.target.value as Formato)}
                className={INPUT_CLS}
              >
                {(Object.keys(FORMATO_LABEL) as Formato[]).map(f => (
                  <option key={f} value={f}>{FORMATO_LABEL[f]}</option>
                ))}
              </select>
            </div>
          </div>

          {/* ID único + link Drive — SÓ Raz (@razconsulting.com.br) enxerga */}
          {isRazEmail && (
            <div className="rounded-xl border border-dashed border-[#C7C3F5] bg-[#F5F4FF] p-3 flex flex-col gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[9px] font-bold uppercase tracking-widest text-[#6E56CF]">Interno Raz</span>
                {initial.contentId ? (
                  <button
                    type="button"
                    onClick={() => navigator.clipboard?.writeText(initial.contentId!)}
                    title="Copiar ID"
                    className="text-xs font-mono font-bold px-2 py-0.5 rounded-md bg-white border border-[#C7C3F5] text-[#4C3AA8] hover:bg-[#EDEBFF]"
                  >
                    {initial.contentId} ⧉
                  </button>
                ) : (
                  <span className="text-[11px] text-zinc-400">sem ID</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="url"
                  value={(form as any).linkDrive ?? initial.linkDrive ?? ""}
                  onChange={e => set("linkDrive" as any, e.target.value as any)}
                  placeholder="Link da pasta no Drive…"
                  className="flex-1 text-xs border border-[#E7E5E0] rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#D6D3CD]"
                />
                {(form.linkDrive ?? initial.linkDrive) && (
                  <a href={(form.linkDrive ?? initial.linkDrive) as string} target="_blank" rel="noopener noreferrer"
                    className="text-xs font-semibold text-[#6E56CF] hover:underline shrink-0">abrir ↗</a>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="url"
                  value={(form as any).linkClickup ?? initial.linkClickup ?? ""}
                  onChange={e => set("linkClickup" as any, e.target.value as any)}
                  placeholder="Link da task no ClickUp…"
                  className="flex-1 text-xs border border-[#E7E5E0] rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#D6D3CD]"
                />
                {(form.linkClickup ?? initial.linkClickup) && (
                  <a href={(form.linkClickup ?? initial.linkClickup) as string} target="_blank" rel="noopener noreferrer"
                    className="text-xs font-semibold text-[#6E56CF] hover:underline shrink-0">abrir ↗</a>
                )}
              </div>
            </div>
          )}

          {/* Linha editorial (pilar) — seletor limpo */}
          <LinhaEditorialPicker
            value={(form.linhaEditorial ?? initial.linhaEditorial ?? "") as string}
            onChange={v => set("linhaEditorial", (v || null) as any)}
          />

          {/* Território temático — seletor limpo (L1–L6) */}
          <TerritorioPicker
            value={((form as any).territorio ?? initial.territorio ?? "") as string}
            onChange={v => set("territorio" as any, (v || null) as any)}
          />

          {/* Segmento / Persona / Funil — Agnes EmiteAí jun/26 */}
          <SegPersonaFunilFields
            segmento={(form.segmento ?? initial.segmento ?? "") as string}
            onSegmento={v => set("segmento", v as any)}
            persona={(form.persona ?? initial.persona ?? "") as string}
            onPersona={v => set("persona", v as any)}
            funil={(form.funil ?? initial.funil ?? "") as any}
            onFunil={v => set("funil", (v || null) as any)}
          />

          <div className="flex items-center gap-3 pt-1">
            <span className="text-[11px] font-bold uppercase tracking-widest text-[var(--brand)]" style={{ fontFamily: "Poppins, sans-serif" }}>Planejamento</span>
            <div className="flex-1 h-px" style={{ background: "var(--solar-line)" }} />
          </div>

          {/* Responsável + Data prevista */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-[#0A0A0A] uppercase tracking-widest mb-1.5">Responsável</label>
              <input
                type="text"
                value={form.responsavel ?? ""}
                onChange={e => set("responsavel", e.target.value || null as any)}
                placeholder="Nome"
                className={INPUT_CLS}
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-[#0A0A0A] uppercase tracking-widest mb-1.5">Data prevista</label>
              <input
                type="date"
                value={form.dataPrevista ?? ""}
                onChange={e => set("dataPrevista", e.target.value || null as any)}
                className={INPUT_CLS}
              />
            </div>
          </div>

          {/* Horário de postagem */}
          <div>
            <label className="block text-[10px] font-bold text-[#0A0A0A] uppercase tracking-widest mb-1.5">Horário de postagem</label>
            <input
              type="text"
              value={(form as any).horarioPostagem ?? initial.horarioPostagem ?? ""}
              onChange={e => set("horarioPostagem" as any, e.target.value as any)}
              placeholder="Ex: 14h às 17h · Manhã · pós-almoço"
              className={INPUT_CLS}
            />
          </div>

          <div className="flex items-center gap-3 pt-1">
            <span className="text-[11px] font-bold uppercase tracking-widest text-[var(--brand)]" style={{ fontFamily: "Poppins, sans-serif" }}>Conteúdo</span>
            <div className="flex-1 h-px" style={{ background: "var(--solar-line)" }} />
          </div>

          {/* Objetivo — Agnes jul/26 (coluna da planilha) */}
          <div>
            <label className="block text-[10px] font-bold text-[#0A0A0A] uppercase tracking-widest mb-1.5">Objetivo</label>
            <textarea
              rows={2}
              value={(form as any).objetivo ?? initial.objetivo ?? ""}
              onChange={e => set("objetivo" as any, e.target.value as any)}
              placeholder="O que esse conteúdo quer alcançar?"
              className={INPUT_CLS + " resize-none"}
            />
          </div>

          {/* Direcionamento do conteúdo — Agnes jul/26 (coluna da planilha) */}
          <div>
            <label className="block text-[10px] font-bold text-[#0A0A0A] uppercase tracking-widest mb-1.5">Direcionamento do conteúdo</label>
            <textarea
              rows={3}
              value={(form as any).direcionamento ?? initial.direcionamento ?? ""}
              onChange={e => set("direcionamento" as any, e.target.value as any)}
              placeholder="Como o conteúdo deve ser construído?"
              className={INPUT_CLS + " resize-none"}
            />
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-[10px] font-bold text-[#0A0A0A] uppercase tracking-widest mb-1.5">Descrição / Briefing</label>
            <textarea
              rows={3}
              value={form.descricao ?? ""}
              onChange={e => set("descricao", e.target.value || null as any)}
              placeholder="Contexto, referências, ideia de copy…"
              className={INPUT_CLS + " resize-none"}
            />
          </div>

          {/* Legenda */}
          <div>
            <label className="block text-[10px] font-bold text-[#0A0A0A] uppercase tracking-widest mb-1.5">Legenda</label>
            <textarea
              rows={4}
              value={form.legenda ?? ""}
              onChange={e => set("legenda", e.target.value || null as any)}
              placeholder="Texto do post, CTAs, emojis…"
              className={INPUT_CLS + " resize-none"}
            />
          </div>

          {/* Observações */}
          <div>
            <label className="block text-[10px] font-bold text-[#0A0A0A] uppercase tracking-widest mb-1.5">Observações</label>
            <textarea
              rows={3}
              value={(form as any).observacoes ?? initial.observacoes ?? ""}
              onChange={e => set("observacoes" as any, e.target.value as any)}
              placeholder="Links de referência, anotações internas, etc."
              className={INPUT_CLS + " resize-none"}
            />
          </div>

          {/* Mídias adicionais movidas pro topo (hero) — esta seção foi removida */}

          {/* Peças relacionadas — stories de engajamento + comentários de porta-voz */}
          <PecasRelacionadasSection
            pauta={initial}
            clientId={initial.clientId}
            onOpenRelated={p => { onOpenRelated?.(p); }}
            onChanged={onSave}
          />

          {/* Histórico de movimentações — quem moveu o card e quando */}
          <div className="flex items-center gap-3 pt-1">
            <span className="text-[11px] font-bold uppercase tracking-widest text-[var(--brand)]" style={{ fontFamily: "Poppins, sans-serif" }}>Atividade</span>
            <div className="flex-1 h-px" style={{ background: "var(--solar-line)" }} />
          </div>

          <AtividadesSection pautaId={initial.id} />

          {/* Comentários — HU-054 */}
          <ComentariosSection pautaId={initial.id} clientId={initial.clientId} />

          {/* Meta info */}
          <div className="pt-2" style={{ borderTop: "1px solid #E7E5E0" }}>
            <div className="flex flex-col gap-1">
              {initial.createdBy && (
                <p className="text-[11px] text-zinc-400">
                  Criado por <span className="text-zinc-500 font-medium">{initial.createdBy}</span>
                  {" "}em {new Date(initial.createdAt).toLocaleDateString("pt-BR")}
                </p>
              )}
              <p className="text-[11px] text-zinc-400">
                Última atualização: {new Date(initial.updatedAt).toLocaleDateString("pt-BR")}
              </p>
            </div>
          </div>

          </div>{/* /px-5 py-4 wrap */}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 shrink-0 flex justify-between items-center gap-2" style={{ borderTop: "1px solid #E7E5E0" }}>
          {confirmAcao ? (
            /* Confirmação inline — imune a bloqueio de popup do navegador */
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-600">
                {confirmAcao === "arquivar" && "Mover para a lixeira?"}
                {confirmAcao === "excluir"  && "Excluir de vez? Não dá pra desfazer."}
                {confirmAcao === "voltar"   && "Voltar pra 'Sugestão de Tema'? A pauta atual vira tema de novo."}
              </span>
              <button
                onClick={() => {
                  if (confirmAcao === "arquivar") { onDelete(initial.id); onClose(); }
                  else if (confirmAcao === "excluir") { onHardDelete?.(initial.id); onClose(); }
                  else if (confirmAcao === "voltar") { onVoltarSugestao?.(initial.id); }
                  setConfirmAcao(null);
                }}
                className="text-xs font-semibold px-2.5 py-1 rounded-lg text-white transition-colors"
                style={{ background: confirmAcao === "excluir" ? "#DC2626" : "#0A0A0A" }}
              >
                {confirmAcao === "excluir" ? "Excluir de vez" : "Confirmar"}
              </button>
              <button
                onClick={() => setConfirmAcao(null)}
                className="text-xs font-medium px-2.5 py-1 rounded-lg border border-[#E7E5E0] text-zinc-600 hover:bg-[#F5F4F1] transition-colors"
              >
                Cancelar
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              {/* Arquivar → lixeira (reversível) */}
              <button
                onClick={() => setConfirmAcao("arquivar")}
                className="flex items-center gap-1.5 text-xs text-red-400 hover:text-red-600 font-medium transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Arquivar
              </button>
              {/* Excluir permanente (só se o caller passar onHardDelete) */}
              {onHardDelete && (
                <button
                  onClick={() => setConfirmAcao("excluir")}
                  className="flex items-center gap-1.5 text-xs text-red-600 hover:text-red-800 font-semibold transition-colors"
                >
                  Excluir
                </button>
              )}
              {/* Voltar pra Sugestão (Agnes/Bela jul/26) */}
              {onVoltarSugestao && (
                <button
                  onClick={() => setConfirmAcao("voltar")}
                  className="flex items-center gap-1.5 text-xs text-[#7C3AED] hover:text-[#5B21B6] font-medium transition-colors"
                >
                  ↩ Voltar pra Sugestão
                </button>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            {/* Checagem de QA — só Raz */}
            {QA_ON && (
              <button
                onClick={() => setQaOpen(true)}
                className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border transition-colors"
                style={qaPend > 0
                  ? { borderColor: "#FCA5A5", background: "#FEF2F2", color: "#B91C1C" }
                  : qaData ? { borderColor: "#A7F3D0", background: "#ECFDF5", color: "#047857" }
                  : { borderColor: "#E7E5E0", background: "#fff", color: "#52525B" }}
                title="Checagem de QA (interno Raz)"
              >
                ✓ QA{qaPend > 0 ? ` · ${qaPend}` : qaData ? " ok" : ""}
              </button>
            )}
            {/* HU-055: abrir no kanban */}
            {location !== "/pautas" && (
              <button
                onClick={() => { onClose(); navigate("/pautas"); }}
                className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg border border-[#E7E5E0] text-[#0A0A0A] hover:bg-[#F5F4F1] transition-colors"
              >
                Abrir no Kanban
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
            <span
              className="inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full"
              style={{ background: rede.bg, color: rede.text }}
            >
              {rede.label}
            </span>
            <span className="inline-flex text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#F5F4F1] text-[#0A0A0A]">
              {FORMATO_LABEL[form.formato ?? initial.formato]}
            </span>
          </div>
        </div>

        {updateMutation.error && (
          <p className="px-5 pb-3 text-xs text-red-500">{updateMutation.error.message}</p>
        )}
      </div>

      {/* QA — painel interno Raz: persistido, aceite por item, revalidar */}
      {qaOpen && QA_ON && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={() => setQaOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[82vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-[#E7E5E0] flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-bold text-[#0A0A0A]" style={{ fontFamily: "Poppins, sans-serif" }}>Checagem de QA</p>
                  <span className="text-[9px] font-bold uppercase tracking-widest text-[#6E56CF] bg-[#F5F4FF] px-1.5 py-0.5 rounded">Interno Raz</span>
                </div>
                <p className="text-xs text-zinc-400 mt-0.5">
                  Português + arte + Direcionador da marca.
                  {qaData?.checkedAt ? ` Última checagem: ${new Date(qaData.checkedAt).toLocaleString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}.` : " Ainda não checado."}
                </p>
              </div>
              <button onClick={() => setQaOpen(false)} className="text-zinc-300 hover:text-zinc-500 shrink-0">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
              {qaChecking && <p className="text-sm text-zinc-400 text-center py-6">Rodando a IA (texto + arte)…</p>}
              {!qaChecking && (!qaData || (qaData.issues ?? []).length === 0) && (
                <p className="text-sm text-emerald-700 text-center py-6">✓ Nenhum ponto encontrado.</p>
              )}
              {!qaChecking && (qaData?.issues ?? []).map((it: any) => (
                <div key={it.id} className={"rounded-xl border p-3 transition-opacity " + (it.resolvido ? "opacity-50 border-[#E7E5E0]" : "border-[#E7E5E0]")}>
                  <div className="flex items-start gap-2.5">
                    <input type="checkbox" checked={!!it.resolvido} onChange={e => toggleQaItem(it.id, e.target.checked)}
                      className="mt-1 w-4 h-4 accent-[#059669] shrink-0" title="Marcar como resolvido" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                          style={{ background: it.severidade === "erro" ? "#FEE2E2" : "#FEF3C7", color: it.severidade === "erro" ? "#B91C1C" : "#92400E" }}>
                          {it.severidade === "erro" ? "Erro" : "Aviso"}
                        </span>
                        <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">{it.categoria}</span>
                      </div>
                      {it.trecho && <p className="text-xs text-zinc-500 italic mb-1">"{it.trecho}"</p>}
                      <p className={"text-sm " + (it.resolvido ? "line-through text-zinc-400" : "text-[#0A0A0A]")}>{it.problema}</p>
                      {it.sugestao && !it.resolvido && <p className="text-[13px] text-emerald-700 mt-1">→ {it.sugestao}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-[#E7E5E0] flex items-center justify-between gap-2">
              <span className="text-xs text-zinc-400">
                {qaData?.issues ? `${qaPend} pendente(s) · ${(qaData.issues.length - qaPend)} resolvido(s)` : ""}
              </span>
              <div className="flex gap-2">
                <button onClick={runQa} disabled={qaChecking}
                  className="px-3 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ background: "#0A0A0A" }}>
                  {qaChecking ? "Rodando…" : qaData ? "Revalidar" : "Rodar checagem"}
                </button>
                <button onClick={() => setQaOpen(false)}
                  className="px-3 py-2 rounded-lg text-sm font-medium border border-[#E7E5E0] text-zinc-600 hover:bg-[#F5F4F1]">
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {desaprovarOpen && (
        <DesaprovarModal
          titulo={form.titulo ?? initial.titulo}
          pending={desaprovarMutation.isPending}
          onCancel={() => setDesaprovarOpen(false)}
          onConfirm={motivo => desaprovarMutation.mutate({ id: initial.id, motivo })}
        />
      )}
    </>
  );
}

// ─── TagsField helper ─────────────────────────────────────────────────────────

function TagsField({ tags: tagsRaw, onChange }: { tags: string[] | null | undefined; onChange: (t: string[]) => void }) {
  // Defensivo: server pode enviar null em dados legados; tipo é string[] mas
  // melhor não confiar — TypeError em produção foi causa de crash em /pautas.
  const tags = tagsRaw ?? [];
  const [input, setInput] = useState("");

  function add() {
    const t = input.trim().replace(/^#/, "");
    if (t && !tags.includes(t)) onChange([...tags, t]);
    setInput("");
  }

  return (
    <div>
      <label className="block text-[10px] font-bold text-[#0A0A0A] uppercase tracking-widest mb-1.5">Tags</label>
      <div className="flex gap-2 mb-2">
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
          placeholder="#tag"
          className={INPUT_CLS}
        />
        <button
          type="button"
          onClick={add}
          className="px-3 py-2 rounded-lg text-sm font-bold text-white"
          style={{ background: "#0A0A0A" }}
        >+</button>
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {tags.map(t => (
            <span key={t} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full text-[#0A0A0A] font-medium" style={{ background: "#F5F4F1" }}>
              #{t}
              <button onClick={() => onChange(tags.filter(x => x !== t))} className="text-[#0A0A0A]/50 hover:text-[#0A0A0A]">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── NovaPautaModal ───────────────────────────────────────────────────────────

// HU-065: upload com barra de progresso (XHR expõe progresso de upload)
function uploadComProgresso(
  file: File,
  token: string,
  onProgress: (pct: number) => void,
): Promise<{ url: string; nome: string; tipo: "imagem" | "video" | "pdf"; tamanho: number }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/upload/media");
    xhr.setRequestHeader("x-client-token", token);
    xhr.upload.onprogress = e => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); }
        catch { reject(new Error("Resposta inválida do servidor")); }
      } else {
        try { reject(new Error(JSON.parse(xhr.responseText).error ?? "Falha no upload")); }
        catch { reject(new Error("Falha no upload")); }
      }
    };
    xhr.onerror = () => reject(new Error("Erro de rede no upload"));
    const fd = new FormData();
    fd.append("file", file);
    xhr.send(fd);
  });
}

function NovaPautaModal({
  colunaInicial,
  clientId,
  onSave,
  onClose,
}: {
  colunaInicial: ColunaPauta;
  clientId: number;
  onSave: () => void;
  onClose: () => void;
}) {
  useEscapeKey(onClose);
  const [titulo, setTitulo] = useState("");
  const COLUNAS_PAUTA = useColumns().filter(c => c.id !== "sugestao_tema") as { id: ColunaPauta; label: string; color: string }[];
  const [descricao, setDescricao] = useState("");
  const [rede, setRede] = useState<Rede>("instagram");
  const [formato, setFormato] = useState<Formato>("feed");
  const [coluna, setColuna] = useState<ColunaPauta>(colunaInicial);
  const [data, setData] = useState("");
  const [responsavel, setResponsavel] = useState("");
  const [linhaEditorial, setLinhaEditorial] = useState("");
  const [segmento, setSegmento] = useState("");
  const [persona, setPersona]   = useState("");
  const [funil, setFunil]       = useState<"" | "T" | "M" | "F">("");
  const [kpiMacro, setKpiMacro] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState<string[]>([]);

  // HU-064/065: mídias enviadas antes de criar a pauta
  type MidiaPend = { url: string; nome: string; tipo: "imagem" | "video" | "pdf"; tamanho: number };
  const [midiasPend, setMidiasPend] = useState<MidiaPend[]>([]);
  const [uploadProgresso, setUploadProgresso] = useState<number | null>(null);
  const [uploadErro, setUploadErro] = useState("");
  const [fileEl, setFileEl] = useState<HTMLInputElement | null>(null);

  const midiaCreateMutation = trpc.midias.create.useMutation();
  const createMutation = trpc.pautas.create.useMutation({
    onSuccess: async (novaPauta: any) => {
      // HU-064/065: registra as mídias enviadas na nova pauta
      for (const m of midiasPend) {
        await midiaCreateMutation.mutateAsync({
          pautaId: novaPauta.id, clientId,
          url: m.url, nome: m.nome, tipo: m.tipo, tamanho: m.tamanho,
        });
      }
      onSave();
      onClose();
    },
  });

  function addTag() {
    const t = tagInput.trim().replace(/^#/, "");
    if (t && !tags.includes(t)) setTags(prev => [...prev, t]);
    setTagInput("");
  }

  async function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    setUploadErro("");
    const raw = localStorage.getItem(CLIENT_AUTH_KEY);
    const token = raw ? (JSON.parse(raw).token ?? "") : "";
    for (const file of files) {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      const isVid = file.type.startsWith("video/") || ["mp4", "mov", "m4v", "webm", "avi", "mkv", "qt", "3gp", "ogv"].includes(ext);
      const isImg = file.type.startsWith("image/") || ["gif", "png", "jpg", "jpeg", "webp", "heic", "heif", "bmp", "svg", "avif", "tiff"].includes(ext);
      const isPdf = file.type === "application/pdf" || ext === "pdf";
      if (!isImg && !isVid && !isPdf) { setUploadErro("Envie imagem, vídeo ou PDF."); continue; }
      if (isImg && file.size > 50 * 1024 * 1024) { setUploadErro(`${file.name}: imagem excede 50MB.`); continue; }
      if (isVid && file.size > 500 * 1024 * 1024) { setUploadErro(`${file.name}: vídeo excede 500MB.`); continue; }
      if (isPdf && file.size > 100 * 1024 * 1024) { setUploadErro(`${file.name}: PDF excede 100MB.`); continue; }
      try {
        const data = await uploadComProgresso(file, token, p => setUploadProgresso(p));
        setMidiasPend(prev => [...prev, { url: data.url, nome: data.nome, tipo: data.tipo, tamanho: data.tamanho }]);
      } catch (err: any) {
        setUploadErro(err.message ?? "Falha no upload");
      } finally {
        setUploadProgresso(null);
      }
    }
  }

  function handleSave() {
    if (!titulo.trim() || createMutation.isPending) return;
    createMutation.mutate({
      clientId,
      titulo: titulo.trim(),
      descricao: descricao.trim() || undefined,
      rede: rede as any,
      formato,
      coluna,
      dataPrevista: data || undefined,
      responsavel: responsavel.trim() || undefined,
      linhaEditorial: linhaEditorial.trim() || undefined,
      segmento: segmento.trim() || undefined,
      persona:  persona || undefined,
      funil:    funil || undefined,
      kpiMacro: kpiMacro || undefined,
      tags,
    });
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-x-4 top-16 bottom-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-lg md:max-h-[90vh] z-50 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ border: "1px solid #E7E5E0" }}>
        <div className="flex items-center justify-between px-6 py-4 shrink-0" style={{ borderBottom: "1px solid #E7E5E0" }}>
          <h2 className="font-bold text-base text-[#0A0A0A]" style={{ fontFamily: "Poppins, sans-serif" }}>Nova pauta</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Título */}
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Título *</label>
            <input autoFocus type="text" value={titulo} onChange={e => setTitulo(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") handleSave(); }}
              placeholder="Do que trata esse conteúdo?" className={INPUT_CLS} />
          </div>

          {/* Linha editorial — HU-063 visual picker */}
          <LinhaEditorialPicker value={linhaEditorial} onChange={setLinhaEditorial} />

          {/* Segmento / Persona / Funil — Agnes EmiteAí jun/26 */}
          <SegPersonaFunilFields
            segmento={segmento} onSegmento={setSegmento}
            persona={persona}   onPersona={setPersona}
            funil={funil}       onFunil={setFunil}
          />

          {/* Descrição */}
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Descrição</label>
            <textarea value={descricao} onChange={e => setDescricao(e.target.value)}
              placeholder="Contexto, referências, ideia de copy…" rows={3}
              className={INPUT_CLS + " resize-none"} />
          </div>

          {/* HU-061: seleção visual de rede */}
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Rede</label>
            <div className="flex gap-2 flex-wrap">
              {(Object.keys(REDE_INFO) as Rede[]).map(r => {
                const info = getRedeInfo(r);
                const isActive = rede === r;
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      setRede(r);
                      const fmts = FORMATOS_POR_REDE[r];
                      if (!fmts.includes(formato)) setFormato(fmts[0]);
                    }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all border-2"
                    style={{
                      background: isActive ? info.bg : "white",
                      borderColor: isActive ? info.color : "#E7E5E0",
                      color: isActive ? info.text : "#888",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    <span>{info.icon}</span>
                    {info.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* HU-062: formatos filtrados por rede */}
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Formato</label>
            <div className="flex gap-2 flex-wrap">
              {FORMATOS_POR_REDE[rede].map(f => {
                const isActive = formato === f;
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setFormato(f)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border"
                    style={{
                      background: isActive ? "#0A0A0A" : "white",
                      borderColor: isActive ? "#0A0A0A" : "#E7E5E0",
                      color: isActive ? "white" : "#888",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    {FORMATO_LABEL[f]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Etapa + Data */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Etapa</label>
              <select value={coluna} onChange={e => setColuna(e.target.value as ColunaPauta)} className={INPUT_CLS}>
                {COLUNAS_PAUTA.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Data prevista</label>
              <input type="date" value={data} onChange={e => setData(e.target.value)} className={INPUT_CLS} />
            </div>
          </div>

          {/* Responsável */}
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Responsável</label>
            <input type="text" value={responsavel} onChange={e => setResponsavel(e.target.value)}
              placeholder="Nome de quem vai produzir" className={INPUT_CLS} />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider mb-1.5">Tags</label>
            <div className="flex gap-2">
              <input type="text" value={tagInput} onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); } }}
                placeholder="#tag" className={INPUT_CLS} />
              <button type="button" onClick={addTag}
                className="px-3 py-2 rounded-lg text-sm font-bold text-white"
                style={{ background: "#0A0A0A" }}>+</button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {tags.map(t => (
                  <span key={t} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full text-[#0A0A0A] font-medium" style={{ background: "#F5F4F1" }}>
                    #{t}
                    <button onClick={() => setTags(prev => prev.filter(x => x !== t))} className="text-[#0A0A0A]/50 hover:text-[#0A0A0A]">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* HU-064/065: upload de mídia no formulário */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider">Mídias</label>
              <button type="button" onClick={() => fileEl?.click()} disabled={uploadProgresso !== null}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border-2 disabled:opacity-50 hover:bg-[#EDEBE6]"
                style={{ borderColor: "#0A0A0A", color: "#0A0A0A" }}>
                + Adicionar
              </button>
              <input ref={setFileEl} type="file" accept="image/*,video/*,application/pdf,.mp4,.mov,.gif,.webm,.m4v,.pdf" multiple className="hidden" onChange={handleFiles} />
            </div>
            <p className="text-[11px] text-zinc-400 mb-2">Imagens até 50MB · Vídeos até 500MB · PDF até 100MB · arraste para reordenar</p>

            {/* HU-065: barra de progresso */}
            {uploadProgresso !== null && (
              <div className="mb-2">
                <div className="h-2 rounded-full bg-[#E7E5E0] overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${uploadProgresso}%`, background: "linear-gradient(90deg,#0A0A0A,#262626)" }} />
                </div>
                <p className="text-[11px] text-[#0A0A0A] mt-1">Enviando… {uploadProgresso}%</p>
              </div>
            )}
            {uploadErro && <p className="text-xs text-red-500 mb-2">{uploadErro}</p>}

            {/* HU-064: lista com reordenação por drag-and-drop */}
            {midiasPend.length > 0 && (
              <DragDropContext
                onDragEnd={(r: DropResult) => {
                  if (!r.destination) return;
                  setMidiasPend(prev => {
                    const arr = [...prev];
                    const [moved] = arr.splice(r.source.index, 1);
                    arr.splice(r.destination!.index, 0, moved);
                    return arr;
                  });
                }}
              >
                <Droppable droppableId="midias-pend" direction="horizontal">
                  {prov => (
                    <div ref={prov.innerRef} {...prov.droppableProps} className="flex gap-2 flex-wrap">
                      {midiasPend.map((m, idx) => (
                        <Draggable key={m.url} draggableId={m.url} index={idx}>
                          {drag => (
                            <div ref={drag.innerRef} {...drag.draggableProps} {...drag.dragHandleProps}
                              className="relative group rounded-lg overflow-hidden border border-[#E7E5E0] bg-[#FAFAFE]"
                              style={{ width: 76, ...drag.draggableProps.style }}>
                              {m.tipo === "imagem"
                                ? <img src={m.url} alt={m.nome} className="w-full h-16 object-cover" />
                                : m.tipo === "pdf"
                                  ? <div className="w-full h-16"><PdfTile compacto /></div>
                                  : <video src={m.url} className="w-full h-16 object-cover" />}
                              {m.tipo === "video" && (
                                <span className="absolute top-0.5 left-0.5 bg-black/60 text-white text-[8px] px-1 rounded">▶</span>
                              )}
                              <button type="button"
                                onClick={() => setMidiasPend(prev => prev.filter((_, i) => i !== idx))}
                                className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100">
                                <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {prov.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        </div>

        {createMutation.error && (
          <p className="px-6 py-2 text-red-500 text-sm">{createMutation.error.message}</p>
        )}

        <div className="flex gap-3 px-6 py-4 shrink-0" style={{ borderTop: "1px solid #E7E5E0" }}>
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-[#E7E5E0] text-[#0A0A0A] text-sm font-semibold hover:bg-[#F5F4F1] transition-colors">
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={!titulo.trim() || createMutation.isPending}
            className="flex-1 py-2.5 rounded-lg text-white text-sm font-semibold transition-all disabled:opacity-40"
            style={{ background: "linear-gradient(90deg, #0A0A0A, #262626)", fontFamily: "Poppins, sans-serif" }}
          >
            {createMutation.isPending ? "Salvando…" : "Salvar pauta"}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── LixeiraModal (HU-056/057) ────────────────────────────────────────────────

function LixeiraModal({
  clientId,
  onClose,
  onRestored,
}: {
  clientId: number;
  onClose: () => void;
  onRestored: () => void;
}) {
  const { data: deleted = [], refetch } = trpc.pautas.listDeleted.useQuery({ clientId });
  const restoreMutation   = trpc.pautas.restore.useMutation({ onSuccess: () => { refetch(); onRestored(); } });
  const hardDeleteMutation = trpc.pautas.hardDelete.useMutation({ onSuccess: () => refetch() });
  // Sugestões de tema descartadas ou recusadas (out/26): antes só a página
  // /temas, desligada, tinha lixeira de tema, e o tema descartado não voltava.
  const { data: temasDel = [], refetch: refetchTemasDel } = trpc.temas.listDeleted.useQuery({ clientId });
  const restoreTemaMutation = trpc.temas.restore.useMutation({ onSuccess: () => { refetchTemasDel(); onRestored(); } });

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-x-4 top-16 bottom-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-md md:max-h-[80vh] z-50 bg-white rounded-2xl shadow-2xl flex flex-col" style={{ border: "1px solid #E7E5E0" }}>
        <div className="flex items-center justify-between px-5 py-4 shrink-0" style={{ borderBottom: "1px solid #E7E5E0" }}>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <h2 className="font-bold text-base text-[#0A0A0A]" style={{ fontFamily: "Poppins, sans-serif" }}>
              Lixeira ({deleted.length + (temasDel as any[]).length})
            </h2>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {(temasDel as any[]).length > 0 && (
            <div className="mb-4">
              <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-2">Sugestões de tema</p>
              <div className="space-y-2">
                {(temasDel as any[]).map((t: any) => (
                  <div key={`t${t.id}`} className="flex items-start gap-3 p-3 rounded-xl border border-[#E7E5E0] bg-[#FAFAFE]">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#0A0A0A] truncate" style={{ fontFamily: "Poppins, sans-serif" }}>
                        {t.codigo ? `${t.codigo} · ` : ""}{t.tema}
                      </p>
                      <p className="text-[11px] text-zinc-400 mt-1">
                        {t.deletedAt ? new Date(t.deletedAt).toLocaleDateString("pt-BR") : ""}
                      </p>
                    </div>
                    <button
                      onClick={() => restoreTemaMutation.mutate({ id: t.id })}
                      disabled={restoreTemaMutation.isPending}
                      className="px-2.5 py-1.5 text-xs font-semibold rounded-lg border-2 transition-all hover:bg-[#EDEBE6] shrink-0"
                      style={{ borderColor: "#0A0A0A", color: "#0A0A0A" }}
                      title="Volta para Sugestão de Tema"
                    >
                      ↩ Restaurar
                    </button>
                  </div>
                ))}
              </div>
              {deleted.length > 0 && <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mt-4 mb-2">Pautas</p>}
            </div>
          )}
          {deleted.length === 0 && (temasDel as any[]).length > 0 ? null : deleted.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 gap-2 text-zinc-400">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm">Lixeira vazia</p>
            </div>
          ) : (
            <div className="space-y-2">
              {deleted.map(p => {
                const rede = getRedeInfo(p.rede as Rede) ?? REDE_INFO.instagram;
                const lc = linhaColor(p.linhaEditorial);
                return (
                  <div key={p.id} className="flex items-start gap-3 p-3 rounded-xl border border-[#E7E5E0] bg-[#FAFAFE]">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#0A0A0A] truncate" style={{ fontFamily: "Poppins, sans-serif" }}>
                        {p.titulo}
                      </p>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background: rede.bg, color: rede.text }}>
                          {rede.label}
                        </span>
                        {p.linhaEditorial && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background: lc.bg, color: lc.color }}>
                            {p.linhaEditorial}
                          </span>
                        )}
                        <span className="text-[11px] text-zinc-400">
                          {p.deletedAt ? new Date(p.deletedAt).toLocaleDateString("pt-BR") : ""}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => restoreMutation.mutate({ id: p.id })}
                        disabled={restoreMutation.isPending}
                        className="px-2.5 py-1.5 text-xs font-semibold rounded-lg border-2 transition-all hover:bg-[#EDEBE6]"
                        style={{ borderColor: "#0A0A0A", color: "#0A0A0A" }}
                        title="Restaurar"
                      >
                        ↩ Restaurar
                      </button>
                      <button
                        onClick={() => { if (confirm("Excluir permanentemente? Isso não pode ser desfeito.")) hardDeleteMutation.mutate({ id: p.id }); }}
                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Excluir permanentemente"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Página Pautas ────────────────────────────────────────────────────────────

export default function Pautas() {
  const { session } = useAuth();
  const clientId = session?.activeClientId ?? session?.clientId ?? 1;

  // Config de coluna por cliente (Opção B). Sem config = quadro padrão.
  const { quadro: quadroCliente, carregado: quadroCarregado } = useQuadro(clientId);
  const COLUNAS = quadroCliente as ColDef[];
  const COLUNAS_PAUTA = COLUNAS.filter(c => c.id !== "sugestao_tema") as { id: ColunaPauta; label: string; color: string }[];

  // Filtros (HU-041~044). viewMode/filtroRede/filtroMes/ordemData persistem por
  // sessão (localStorage) pra não zerar a cada reload/navegação. Filtros que
  // dependem do cliente (linha/coluna/perfil) NÃO persistem, pra não vazar
  // valor de um cliente pro outro no switcher.
  const _viewPrefs = (() => {
    try { return JSON.parse(localStorage.getItem("sh_pautas_view") || "{}"); } catch { return {}; }
  })();
  const [filtroRede, setFiltroRede]           = useState<Rede | "todas">(_viewPrefs.filtroRede ?? "todas");
  const [filtroLinha, setFiltroLinha]         = useState<string>("todas");
  const [filtroMes, setFiltroMes]             = useState<string>(_viewPrefs.filtroMes ?? "todos");
  const [filtroColuna, setFiltroColuna]       = useState<Coluna | "todas">("todas");
  const [filtroPerfil, setFiltroPerfil]       = useState<string>("todos");   // HU-044
  const [ordemData, setOrdemData]             = useState<"none" | "asc" | "desc">(_viewPrefs.ordemData ?? "none"); // HU-045
  const [historicoRecolhido, setHistoricoRecolhido] = useState(true);        // HU-039

  const [novaColuna, setNovaColuna]   = useState<ColunaPauta | null>(null);
  const [quickAddCol, setQuickAddCol] = useState<ColunaPauta | null>(null);
  const [quickAddText, setQuickAddText] = useState("");
  const [detalhe, setDetalhe]         = useState<Pauta | null>(null);
  const [duplicarAlvo, setDuplicarAlvo] = useState<Pauta | null>(null);
  const [lixeiraOpen, setLixeiraOpen] = useState(false);
  // Importar / exportar planilha (out/26): só o time Raz.
  const [importOpen, setImportOpen] = useState(false);
  const isRazUser = (session?.email ?? "").toLowerCase().trim().endsWith("@razconsulting.com.br");
  // Modal local de tema (jun/26 — Agnes matou a página /temas)
  const [temaModal, setTemaModal]     = useState<"new" | { id: number } | null>(null);

  // Modo seleção + export PPT (HU-058/059/086/087)
  const [modoSelecao, setModoSelecao]   = useState(false);
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set());
  const [viewMode, setViewMode]         = useState<"kanban" | "tabela">(_viewPrefs.viewMode ?? "kanban");
  React.useEffect(() => {
    try { localStorage.setItem("sh_pautas_view", JSON.stringify({ viewMode, filtroRede, filtroMes, ordemData })); } catch { /* ignore */ }
  }, [viewMode, filtroRede, filtroMes, ordemData]);

  // Recolher colunas do kanban (pedido João). Persiste por viewer.
  const [colsColapsadas, setColsColapsadas] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem("sh_cols_colapsadas") || "[]")); } catch { return new Set(); }
  });
  const toggleColColapsada = (id: string) => setColsColapsadas(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    try { localStorage.setItem("sh_cols_colapsadas", JSON.stringify([...next])); } catch { /* ignore */ }
    return next;
  });

  // Saved views: presets nomeados de filtro+visão, por viewer (localStorage).
  const [savedViews, setSavedViews] = useState<{ id: string; name: string; cfg: any }[]>(() => {
    try { return JSON.parse(localStorage.getItem("sh_pautas_saved_views") || "[]"); } catch { return []; }
  });
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  function persistViews(v: { id: string; name: string; cfg: any }[]) {
    setSavedViews(v);
    try { localStorage.setItem("sh_pautas_saved_views", JSON.stringify(v)); } catch { /* ignore */ }
  }
  function applyView(v: { id: string; cfg: any }) {
    const c = v.cfg || {};
    setViewMode(c.viewMode ?? "kanban");
    setFiltroRede(c.filtroRede ?? "todas");
    setFiltroMes(c.filtroMes ?? "todos");
    setFiltroColuna(c.filtroColuna ?? "todas");
    setOrdemData(c.ordemData ?? "none");
    setActiveViewId(v.id);
  }
  function saveCurrentView() {
    const name = window.prompt("Nome da view (ex: A publicar esta semana):");
    if (!name || !name.trim()) return;
    const v = { id: String(Date.now()), name: name.trim(), cfg: { viewMode, filtroRede, filtroMes, filtroColuna, ordemData } };
    persistViews([...savedViews, v]);
    setActiveViewId(v.id);
  }
  function deleteView(id: string) {
    persistViews(savedViews.filter(v => v.id !== id));
    if (activeViewId === id) setActiveViewId(null);
  }

  const bulkMoveMutation = trpc.pautas.move.useMutation();
  async function moverSelecionados(coluna: ColunaPauta) {
    const ids = Array.from(selecionados);
    if (!ids.length) return;
    const falhas: string[] = [];
    for (const id of ids) {
      try { await bulkMoveMutation.mutateAsync({ id, coluna }); }
      catch (e: any) { falhas.push(e?.message ?? "erro"); }
    }
    setSelecionados(new Set()); setModoSelecao(false); refetch();
    if (falhas.length) alert(`${falhas.length} de ${ids.length} cards não foram movidos: ${Array.from(new Set(falhas)).join(" / ")}`);
  }
  // Bulk edit: aplica um campo (rede, data, etc) a todas as selecionadas.
  async function bulkSetField(patch: Record<string, any>) {
    const ids = Array.from(selecionados);
    if (!ids.length) return;
    for (const id of ids) { try { await updateFieldMutation.mutateAsync({ id, ...patch } as any); } catch { /* segue */ } }
  }
  const [exportando, setExportando]     = useState(false);

  const { data: pautas = [], isLoading, refetch } = trpc.pautas.list.useQuery(
    { clientId },
    { enabled: !!clientId }
  );

  // Deep-link: /pautas?card=ID abre o card direto (link compartilhável).
  const deepLinkDone = useRef(false);
  useEffect(() => {
    if (deepLinkDone.current || !pautas.length) return;
    const cardId = new URLSearchParams(window.location.search).get("card");
    if (!cardId) { deepLinkDone.current = true; return; }
    const alvo = (pautas as Pauta[]).find(p => p.id === Number(cardId) || p.contentId === cardId);
    if (alvo) { setDetalhe(alvo); deepLinkDone.current = true; }
  }, [pautas]);

  // Nível 1: a URL do navegador reflete o card aberto (copiar a barra = link).
  const firstSync = useRef(true);
  useEffect(() => {
    if (firstSync.current) { firstSync.current = false; if (!detalhe) return; }
    const params = new URLSearchParams(window.location.search);
    if (detalhe) params.set("card", detalhe.contentId || String(detalhe.id));
    else params.delete("card");
    const qs = params.toString();
    window.history.replaceState(null, "", `/pautas${qs ? `?${qs}` : ""}`);
  }, [detalhe]);

  // Primeira mídia de cada pauta (pra thumbnail no card do kanban)
  const { data: primeirasMidias = [] } = trpc.midias.firstByClient.useQuery(
    { clientId },
    { enabled: !!clientId }
  );
  const thumbByPauta = useMemo(() => {
    const map: Record<number, { url: string; tipo: string }> = {};
    for (const m of primeirasMidias) map[m.pautaId] = { url: m.url, tipo: m.tipo };
    return map;
  }, [primeirasMidias]);

  const utils = trpc.useUtils();
  // Move otimista: o card pula de coluna na hora e só reverte se o servidor
  // falhar (antes era refetch completo, o board piscava e o card "voltava").
  const moveMutation = trpc.pautas.move.useMutation({
    onMutate: async ({ id, coluna }) => {
      await utils.pautas.list.cancel({ clientId });
      const prev = utils.pautas.list.getData({ clientId });
      utils.pautas.list.setData({ clientId }, (old: any) =>
        Array.isArray(old) ? old.map((p: any) => (p.id === id ? { ...p, coluna } : p)) : old
      );
      return { prev };
    },
    onError: (_e, _vars, ctx: any) => { if (ctx?.prev) utils.pautas.list.setData({ clientId }, ctx.prev); },
    onSettled: () => { utils.pautas.list.invalidate({ clientId }); },
  });
  // Edição inline otimista na Tabela (rede, formato, data prevista).
  const updateFieldMutation = trpc.pautas.update.useMutation({
    onMutate: async (vars: any) => {
      await utils.pautas.list.cancel({ clientId });
      const prev = utils.pautas.list.getData({ clientId });
      utils.pautas.list.setData({ clientId }, (old: any) =>
        Array.isArray(old) ? old.map((p: any) => (p.id === vars.id ? { ...p, ...vars } : p)) : old
      );
      return { prev };
    },
    onError: (_e: any, _v: any, ctx: any) => { if (ctx?.prev) utils.pautas.list.setData({ clientId }, ctx.prev); },
    onSettled: () => { utils.pautas.list.invalidate({ clientId }); },
  });
  // Quick-add inline no rodapé da coluna (cria direto, sem abrir o modal completo).
  const quickAddMutation = trpc.pautas.create.useMutation({
    onSuccess: () => { utils.pautas.list.invalidate({ clientId }); },
  });
  const desaprovarMutation = trpc.pautas.desaprovar.useMutation({
    onSuccess: () => { setDesaprovarAlvo(null); refetch(); },
  });
  const [desaprovarAlvo, setDesaprovarAlvo] = useState<Pauta | null>(null);
  const deleteMutation = trpc.pautas.delete.useMutation({ onSuccess: () => refetch() });
  const hardDeletePautaMutation = trpc.pautas.hardDelete.useMutation({ onSuccess: () => refetch() });

  // ── Temas na coluna "Sugestão de Tema" + feature flags por cliente ─────────
  // Vanessa (EmiteAí, jun/26) pediu pra aprovar temas direto pelo kanban.
  // Carregamos temas com status=sugestao e renderizamos na coluna #0.
  const { data: todosTemas = [], refetch: refetchTemas } = trpc.temas.list.useQuery(
    { clientId },
    { enabled: !!clientId }
  );
  const temasSugestao = useMemo(
    () => (todosTemas as any[]).filter(t => t.status === "sugestao"),
    [todosTemas]
  );
  // Quantas sugestões apontam para cada código de tema (comentário, story).
  const filhosTemaMap = useMemo(() => {
    const m: Record<string, number> = {};
    for (const t of temasSugestao as any[]) {
      const k = (t.parentCodigo ?? "").trim().toLowerCase();
      if (k) m[k] = (m[k] ?? 0) + 1;
    }
    return m;
  }, [temasSugestao]);
  const aprovarTemaMutation  = trpc.temas.aprovar.useMutation({
    onSuccess: () => { refetchTemas(); refetch(); }
  });
  const descartarTemaMutation = trpc.temas.delete.useMutation({
    onSuccess: () => refetchTemas()
  });
  // Recusa de Sugestão com motivo (pedido Sophia jul/26) — pop-up obrigatório
  const temaDesaprovarMutation = trpc.temas.desaprovar.useMutation({
    onSuccess: () => { setTemaRecusaAlvo(null); refetchTemas(); },
  });
  const [temaRecusaAlvo, setTemaRecusaAlvo] = useState<{ id: number; titulo: string } | null>(null);
  // Jul/26 (Agnes/Bela): desfazer aprovação — pauta volta pra Sugestão de Tema
  const voltarSugestaoMutation = trpc.pautas.voltarParaSugestao.useMutation({
    onSuccess: () => { refetch(); refetchTemas(); },
  });

  // ── Feature flag: hide_roteiro_column (EmiteAí, sugestão Vanessa) ─────────
  const { data: featureFlags = [] } = trpc.featureFlags.list.useQuery(
    { clientId },
    { enabled: !!clientId, staleTime: 5 * 60_000 }
  );
  const hideRoteiroColumn = (featureFlags as any[]).some(
    f => f.featureKey === "hide_roteiro_column" && f.enabled
  );

  // Opções dinâmicas de filtro
  const mesesDisponiveis = useMemo(() => {
    const set = new Set<string>();
    for (const p of pautas) {
      if (p.dataPrevista) set.add(p.dataPrevista.slice(0, 7)); // "YYYY-MM"
    }
    // Sugestões de tema também têm data (out/26: o plano de novembro entra todo em sugestão).
    for (const t of temasSugestao as any[]) {
      if (t.dataPub && /^\d{4}-\d{2}/.test(t.dataPub)) set.add(String(t.dataPub).slice(0, 7));
    }
    return Array.from(set).sort();
  }, [pautas, temasSugestao]);

  // Filtros de mês e linha valem também para a coluna Sugestão de Tema.
  const temasSugestaoVisiveis = useMemo(() => (temasSugestao as any[]).filter(t => {
    // Sugestão sem data continua visível com filtro de mês (senão some sem aviso).
    if (filtroMes !== "todos" && t.dataPub && !String(t.dataPub).startsWith(filtroMes)) return false;
    if (filtroLinha !== "todas" && t.linhaEditorial !== filtroLinha) return false;
    return true;
  }), [temasSugestao, filtroMes, filtroLinha]);

  const linhasDisponiveis = useMemo(() => {
    const set = new Set<string>();
    for (const p of pautas) {
      if (p.linhaEditorial) set.add(p.linhaEditorial);
    }
    return Array.from(set).sort();
  }, [pautas]);

  // HU-044: perfis alvo disponíveis
  const perfisDisponiveis = useMemo(() => {
    const set = new Set<string>();
    for (const p of pautas) {
      if (p.perfilAlvo) set.add(p.perfilAlvo);
    }
    return Array.from(set).sort();
  }, [pautas]);

  // Quantas peças relacionadas cada post-mãe tem (p/ badge no card do board).
  const childCountMap = useMemo(() => {
    const m: Record<number, number> = {};
    for (const p of pautas) if (p.parentId != null) m[p.parentId] = (m[p.parentId] ?? 0) + 1;
    return m;
  }, [pautas]);

  const etapaVisivel = (id: string) =>
    COLUNAS_PAUTA.some(c => c.id === id) && !(id === "roteiro" && hideRoteiroColumn);
  // Filtro de etapa que não existe no quadro do cliente ativo (troca de
  // cliente, view salva), ou "Fora do quadro" sem nenhum card nessa situação,
  // volta para "todas" em vez de esvaziar o quadro.
  useEffect(() => {
    if (!quadroCarregado || filtroColuna === "todas") return;
    if ((filtroColuna as string) === COLUNA_FORA) {
      if (!pautas.some(p => !etapaVisivel(p.coluna))) setFiltroColuna("todas");
      return;
    }
    if (!COLUNAS_PAUTA.some(c => c.id === filtroColuna)) setFiltroColuna("todas");
  }, [quadroCarregado, filtroColuna, COLUNAS_PAUTA, pautas, hideRoteiroColumn]);
  const pautasFiltradas = useMemo(() => {
    const arr = pautas.filter(p => {
      if (filtroRede !== "todas" && p.rede !== filtroRede) return false;
      if (filtroLinha !== "todas" && p.linhaEditorial !== filtroLinha) return false;
      if (filtroMes !== "todos" && (!p.dataPrevista || !p.dataPrevista.startsWith(filtroMes))) return false;
      if (filtroColuna === (COLUNA_FORA as any)) {
        if (etapaVisivel(p.coluna)) return false;
      } else if (filtroColuna !== "todas" && p.coluna !== filtroColuna) return false;
      if (filtroPerfil !== "todos" && p.perfilAlvo !== filtroPerfil) return false;
      return true;
    });
    // HU-045: ordenação por data de publicação
    if (ordemData !== "none") {
      arr.sort((a, b) => {
        const cmp = (a.dataPrevista ?? "").localeCompare(b.dataPrevista ?? "");
        return ordemData === "asc" ? cmp : -cmp;
      });
    }
    return arr;
  }, [pautas, filtroRede, filtroLinha, filtroMes, filtroColuna, filtroPerfil, ordemData, COLUNAS_PAUTA, hideRoteiroColumn]);

  function handleMove(id: number, coluna: ColunaPauta) {
    if (coluna === "desaprovado") { // pop-up de motivo obrigatório
      const alvo = (pautas as Pauta[]).find(p => p.id === id) ?? null;
      setDesaprovarAlvo(alvo ?? ({ id, titulo: "" } as Pauta));
      return;
    }
    moveMutation.mutate({ id, coluna });
  }

  function handleDelete(id: number) {
    deleteMutation.mutate({ id });
  }

  function handleDetailSave() {
    refetch();
  }

  function toggleSelecao(id: number) {
    setSelecionados(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function sairModoSelecao() {
    setModoSelecao(false);
    setSelecionados(new Set());
  }

  function selecionarTodos() {
    setModoSelecao(true);
    setSelecionados(new Set(pautasFiltradas.map(p => p.id)));
  }

  async function exportarPptx() {
    if (selecionados.size === 0) return;
    setExportando(true);
    try {
      const raw = localStorage.getItem(CLIENT_AUTH_KEY);
      const token = raw ? (JSON.parse(raw).token ?? "") : "";
      const res = await fetch("/api/export/pautas-pptx", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-client-token": token },
        body: JSON.stringify({ pautaIds: Array.from(selecionados), clientId }),
      });
      if (!res.ok) throw new Error("Erro ao gerar PPTX");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pautas-emiteai-${new Date().toISOString().slice(0, 10)}.pptx`;
      a.click();
      URL.revokeObjectURL(url);
      sairModoSelecao();
    } catch (e: any) {
      alert("Erro ao exportar: " + e.message);
    } finally {
      setExportando(false);
    }
  }

  const REDES_FILTER: { value: Rede | "todas"; label: string }[] = [
    { value: "todas",     label: "Todas" },
    { value: "instagram", label: "Instagram" },
    { value: "tiktok",    label: "TikTok" },
    { value: "youtube",   label: "YouTube" },
    { value: "linkedin",  label: "LinkedIn" },
    { value: "twitter",   label: "Twitter/X" },
  ];

  const hasFilters = filtroRede !== "todas" || filtroLinha !== "todas" || filtroMes !== "todos" || filtroColuna !== "todas" || filtroPerfil !== "todos";

  // HU-039: só "desaprovado" fica no histórico recolhível.
  // "publicado" é a coluna terminal positiva e deve aparecer sempre — é onde a Thainá
  // (e o resto do time) acompanha o que foi pro ar. Esconder por padrão estava confundindo.
  const COLUNAS_HISTORICO: Coluna[] = ["desaprovado"];
  // Card em etapa que não aparece no quadro (etapa fora da config do cliente,
  // ou Roteiro escondido pela flag): ganha a coluna "Fora do quadro", que só
  // existe enquanto houver card assim. Nada some da tela (out/26, Agnes).
  const pautasForaDoQuadro = pautasFiltradas.filter(p => !etapaVisivel(p.coluna));
  const colunasVisiveis = !quadroCarregado ? [] : [
    ...(pautasForaDoQuadro.length > 0
      ? [{ id: COLUNA_FORA as any, label: "Fora do quadro", color: "#B42318" } as ColDef]
      : []),
    ...COLUNAS.filter(c => {
      if (historicoRecolhido && COLUNAS_HISTORICO.includes(c.id)) return false;
      // Esconde "Roteiro" quando o cliente tem a flag hide_roteiro_column ativa.
      if (c.id === "roteiro" && hideRoteiroColumn) return false;
      return true;
    }),
  ];

  // HU-059: selecionar/desselecionar todos os cards de uma coluna
  function toggleColuna(colId: Coluna) {
    const idsColuna = pautasFiltradas.filter(p => p.coluna === colId).map(p => p.id);
    setSelecionados(prev => {
      const next = new Set(prev);
      const todosSelecionados = idsColuna.every(id => next.has(id));
      if (todosSelecionados) idsColuna.forEach(id => next.delete(id));
      else idsColuna.forEach(id => next.add(id));
      return next;
    });
  }

  return (
    <ColumnsCtx.Provider value={COLUNAS}>
    <div className="h-full flex flex-col bg-[var(--solar-bg)]">
      {/* Header */}
      <div className="bg-white border-b border-[var(--solar-line)] px-6 py-4 shrink-0">
        <div className="flex items-center gap-4 flex-wrap gap-y-3">
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-lg text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>
              Pautas
            </h1>
            <p className="text-[var(--solar-mute)] text-xs mt-0.5">
              {pautasFiltradas.length} de {pautas.length} {pautas.length === 1 ? "pauta" : "pautas"} · {COLUNAS.length} etapas
            </p>
          </div>

          {modoSelecao ? (
            <div className="flex items-center gap-2 shrink-0 flex-wrap">
              <span className="text-xs font-semibold text-zinc-500">{selecionados.size} selecionada{selecionados.size === 1 ? "" : "s"}</span>
              <button onClick={selecionarTodos}
                className="px-2.5 py-2 text-xs font-semibold rounded-lg border border-[#E7E5E0] text-[#0A0A0A] hover:bg-[#F5F4F1]">
                Todos ({pautasFiltradas.length})
              </button>
              {selecionados.size > 0 && (
                <select
                  value=""
                  onChange={e => { if (e.target.value) moverSelecionados(e.target.value as ColunaPauta); }}
                  className="px-2.5 py-2 text-xs font-semibold rounded-lg border border-[#0A0A0A] text-[#0A0A0A] bg-white cursor-pointer"
                  title="Mover selecionadas para uma coluna"
                >
                  <option value="">Mover para…</option>
                  {COLUNAS_PAUTA.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              )}
              {selecionados.size > 0 && (
                <select value="" onChange={e => { if (e.target.value) { bulkSetField({ rede: e.target.value }); e.currentTarget.value = ""; } }}
                  className="px-2.5 py-2 text-xs font-semibold rounded-lg border border-[#E7E5E0] text-[#0A0A0A] bg-white cursor-pointer" title="Definir rede das selecionadas">
                  <option value="">Rede…</option>
                  {(Object.keys(REDE_INFO) as Rede[]).map(r => <option key={r} value={r}>{getRedeInfo(r).label}</option>)}
                </select>
              )}
              {selecionados.size > 0 && (
                <input type="date" onChange={e => { if (e.target.value) bulkSetField({ dataPrevista: e.target.value }); }}
                  className="px-2.5 py-2 text-xs font-semibold rounded-lg border border-[#E7E5E0] text-[#0A0A0A] bg-white cursor-pointer" title="Definir data das selecionadas" />
              )}
              <button
                onClick={exportarPptx}
                disabled={selecionados.size === 0 || exportando}
                className="flex items-center gap-1.5 px-3 py-2 text-white text-xs font-semibold rounded-lg transition-all hover:opacity-90 disabled:opacity-40"
                style={{ background: "linear-gradient(90deg, #0A0A0A, #262626)" }}
              >
                {exportando ? "Gerando…" : `PPT (${selecionados.size})`}
              </button>
              <button onClick={sairModoSelecao}
                className="px-3 py-2 text-xs font-semibold rounded-lg border border-[#E7E5E0] text-zinc-500 hover:bg-[#F5F4F1]">
                Cancelar
              </button>
            </div>
          ) : (
            <>
              {/* Toggle Kanban / Tabela */}
              <div className="flex items-center gap-1 bg-[#F5F4F1] rounded-lg p-1 shrink-0">
                <button onClick={() => setViewMode("kanban")} className="px-2.5 py-1.5 text-xs font-semibold rounded-md" style={{ background: viewMode === "kanban" ? "#fff" : "transparent", color: viewMode === "kanban" ? "#0A0A0A" : "#71717A" }}>Kanban</button>
                <button onClick={() => setViewMode("tabela")} className="px-2.5 py-1.5 text-xs font-semibold rounded-md" style={{ background: viewMode === "tabela" ? "#fff" : "transparent", color: viewMode === "tabela" ? "#0A0A0A" : "#71717A" }}>Tabela</button>
              </div>
              <button
                onClick={() => setModoSelecao(true)}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-[#E7E5E0] text-[#0A0A0A] hover:bg-[#F5F4F1] transition-all shrink-0"
              >
                ☑ Selecionar
              </button>
              {/* Exportar PPT */}
              <button
                onClick={() => setModoSelecao(true)}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-[#E7E5E0] text-[#0A0A0A] hover:bg-[#F5F4F1] transition-all shrink-0"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Exportar PPT
              </button>

              {/* Importar / exportar planilha (só Raz) */}
              {isRazUser && (
                <button
                  onClick={() => setImportOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-[#E7E5E0] text-[#0A0A0A] hover:bg-[#F5F4F1] transition-all shrink-0"
                  title="Baixar o modelo de planilha ou subir sugestões de tema e cards"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                  Importar / exportar
                </button>
              )}

              {/* Lixeira */}
              <button
                onClick={() => setLixeiraOpen(true)}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-[#E7E5E0] text-zinc-400 hover:text-red-400 hover:border-red-200 hover:bg-red-50 transition-all shrink-0"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Lixeira
              </button>

              <button
                onClick={() => setNovaColuna(etapaInicial(COLUNAS) as ColunaPauta)}
                className="flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg transition-all hover:opacity-90 shrink-0"
                style={{ background: "linear-gradient(90deg, #0A0A0A, #262626)", fontFamily: "Poppins, sans-serif" }}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Nova pauta
              </button>
            </>
          )}
        </div>

        {/* Aviso de modo seleção */}
        {modoSelecao && (
          <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg text-xs" style={{ background: "#EDEBE6", color: "#0A0A0A" }}>
            <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-medium">
              Clique nas pautas para selecioná-las · {selecionados.size} selecionada{selecionados.size === 1 ? "" : "s"}
            </span>
          </div>
        )}

        {/* Saved views — presets de filtro+visão */}
        <div className="flex items-center gap-1.5 mt-3 flex-wrap">
          <button
            onClick={() => { setActiveViewId(null); setFiltroRede("todas"); setFiltroMes("todos"); setFiltroColuna("todas"); setOrdemData("none"); }}
            className="px-2.5 py-1 rounded-full text-xs font-medium transition-colors"
            style={{ background: activeViewId === null ? "var(--solar-ink)" : "transparent", color: activeViewId === null ? "#fff" : "var(--solar-mute)", border: "1px solid " + (activeViewId === null ? "var(--solar-ink)" : "var(--solar-line)") }}
          >
            Tudo
          </button>
          {savedViews.map(v => (
            <span key={v.id} className="group inline-flex items-center rounded-full text-xs border transition-colors"
              style={{ background: activeViewId === v.id ? "var(--brand-soft)" : "transparent", borderColor: activeViewId === v.id ? "var(--brand)" : "var(--solar-line)" }}>
              <button onClick={() => applyView(v)} className="pl-2.5 pr-1.5 py-1 font-medium" style={{ color: activeViewId === v.id ? "var(--brand-ink)" : "var(--solar-ink)" }}>{v.name}</button>
              <button onClick={() => deleteView(v.id)} title="Remover view" className="w-5 h-5 mr-0.5 rounded-full text-[var(--solar-mute)] hover:text-red-500 flex items-center justify-center text-[13px] leading-none">×</button>
            </span>
          ))}
          <button onClick={saveCurrentView} title="Salvar filtros atuais como uma view"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium text-[var(--brand-ink)] hover:bg-[var(--brand-soft)] transition-colors"
            style={{ border: "1px dashed var(--brand)" }}>
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
            Salvar view
          </button>
        </div>

        {/* Filtros — os mais usados: canal, etapa, mês */}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {/* Canal (rede) */}
          <div className="flex items-center gap-1 bg-[var(--solar-bg)] rounded-lg p-1 overflow-x-auto">
            {REDES_FILTER.map(r => (
              <button
                key={r.value}
                onClick={() => setFiltroRede(r.value)}
                className="px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap"
                style={{
                  fontFamily: "Poppins, sans-serif",
                  background: filtroRede === r.value ? "#fff" : "transparent",
                  color: filtroRede === r.value ? "var(--solar-ink)" : "var(--solar-mute)",
                  boxShadow: filtroRede === r.value ? "var(--elev-1)" : "none",
                }}
              >
                {r.label}
              </button>
            ))}
          </div>

          {/* Mês */}
          {mesesDisponiveis.length > 0 && (
            <select
              value={filtroMes}
              onChange={e => setFiltroMes(e.target.value)}
              className="text-xs border border-[#E7E5E0] rounded-lg px-2.5 py-1.5 text-[#0A0A0A] focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] bg-white"
            >
              <option value="todos">Mês</option>
              {mesesDisponiveis.map(m => {
                const [y, mo] = m.split("-");
                const label = new Date(Number(y), Number(mo) - 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
                return <option key={m} value={m}>{label}</option>;
              })}
            </select>
          )}

          {/* Etapa */}
          <select
            value={filtroColuna}
            onChange={e => setFiltroColuna(e.target.value as Coluna | "todas")}
            className="text-xs border border-[#E7E5E0] rounded-lg px-2.5 py-1.5 text-[#0A0A0A] focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] bg-white"
          >
            <option value="todas">Etapa</option>
            {COLUNAS_PAUTA.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            {pautas.some(p => !etapaVisivel(p.coluna)) && <option value={COLUNA_FORA}>Fora do quadro</option>}
          </select>

          {/* HU-045: Ordenar por data */}
          <button
            onClick={() => setOrdemData(o => o === "none" ? "asc" : o === "asc" ? "desc" : "none")}
            className="text-xs border border-[#E7E5E0] rounded-lg px-2.5 py-1.5 text-[#0A0A0A] hover:bg-[#F5F4F1] transition-colors flex items-center gap-1"
            title="Ordenar cards por data de publicação"
          >
            Data {ordemData === "asc" ? "▲" : ordemData === "desc" ? "▼" : "⇅"}
          </button>

          {/* HU-039: Histórico */}
          <button
            onClick={() => setHistoricoRecolhido(h => !h)}
            className="text-xs border border-[#E7E5E0] rounded-lg px-2.5 py-1.5 hover:bg-[#F5F4F1] transition-colors flex items-center gap-1"
            style={{ color: historicoRecolhido ? "#888" : "#0A0A0A" }}
            title="Mostrar/ocultar coluna de Desaprovado"
          >
            {historicoRecolhido ? "▸ Mostrar desaprovados" : "▾ Ocultar desaprovados"}
          </button>

          {/* Limpar filtros */}
          {hasFilters && (
            <button
              onClick={() => { setFiltroRede("todas"); setFiltroLinha("todas"); setFiltroMes("todos"); setFiltroColuna("todas"); setFiltroPerfil("todos"); }}
              className="text-xs text-[#0A0A0A] underline underline-offset-2 hover:opacity-70 transition-opacity"
            >
              Limpar filtros
            </button>
          )}
        </div>
      </div>

      {/* Kanban / Tabela */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 rounded-full border-2 border-[#E7E5E0] border-t-[#0A0A0A] animate-spin" />
        </div>
      ) : viewMode === "tabela" ? (
        <div className="flex-1 overflow-auto p-6">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 border-b border-[#E7E5E0]">
                <th className="py-2 px-2 w-8">
                  <input type="checkbox"
                    checked={pautasFiltradas.length > 0 && pautasFiltradas.every(p => selecionados.has(p.id))}
                    onChange={e => { if (e.target.checked) selecionarTodos(); else sairModoSelecao(); }}
                    className="cursor-pointer accent-[#0A0A0A]" />
                </th>
                <th className="py-2 px-2">Título</th>
                <th className="py-2 px-2">Etapa</th>
                <th className="py-2 px-2">Data prevista</th>
                <th className="py-2 px-2">Rede</th>
                <th className="py-2 px-2">Formato</th>
                <th className="py-2 px-2">Persona</th>
              </tr>
            </thead>
            <tbody>
              {pautasFiltradas.map(p => {
                const ci = colunaInfo(p.coluna, COLUNAS);
                const sel = selecionados.has(p.id);
                return (
                  <tr key={p.id} className="border-b border-[#F0EEEA] hover:bg-[#FAFAFE] transition-colors" style={{ background: sel ? "#F3F0FF" : undefined }}>
                    <td className="py-2 px-2">
                      <input type="checkbox" checked={sel}
                        onChange={() => { setModoSelecao(true); toggleSelecao(p.id); }}
                        className="cursor-pointer accent-[#0A0A0A]" />
                    </td>
                    <td className="py-2 px-2 font-medium text-[#0A0A0A] cursor-pointer hover:underline" onClick={() => setDetalhe(p)}>{p.titulo}</td>
                    <td className="py-2 px-2">
                      <select
                        value={p.coluna}
                        onChange={e => handleMove(p.id, e.target.value as ColunaPauta)}
                        title="Mudar etapa"
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white whitespace-nowrap border-0 cursor-pointer appearance-none focus:outline-none focus:ring-2 focus:ring-[var(--brand)]"
                        style={{ background: ci.color }}
                      >
                        {!COLUNAS_PAUTA.some(c => c.id === p.coluna) && (
                          <option value={p.coluna} disabled style={{ background: "#fff", color: "#B42318" }}>{colunaInfo(p.coluna, COLUNAS).label}</option>
                        )}
                        {COLUNAS_PAUTA.map(c => <option key={c.id} value={c.id} style={{ background: "#fff", color: "#0A0A0A" }}>{c.label}</option>)}
                      </select>
                    </td>
                    <td className="py-2 px-2">
                      <input
                        type="date"
                        value={p.dataPrevista?.slice(0, 10) ?? ""}
                        onChange={e => updateFieldMutation.mutate({ id: p.id, dataPrevista: e.target.value || null } as any)}
                        className="bg-transparent text-zinc-600 text-xs border border-transparent hover:border-[var(--solar-line)] focus:border-[var(--brand)] rounded px-1 py-0.5 cursor-pointer focus:outline-none tabular-nums"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <select value={p.rede} onChange={e => updateFieldMutation.mutate({ id: p.id, rede: e.target.value } as any)}
                        className="bg-transparent text-zinc-600 text-xs border border-transparent hover:border-[var(--solar-line)] focus:border-[var(--brand)] rounded px-1 py-0.5 cursor-pointer focus:outline-none capitalize">
                        {(Object.keys(REDE_INFO) as Rede[]).map(r => <option key={r} value={r}>{getRedeInfo(r).label}</option>)}
                      </select>
                    </td>
                    <td className="py-2 px-2">
                      <select value={p.formato} onChange={e => updateFieldMutation.mutate({ id: p.id, formato: e.target.value } as any)}
                        className="bg-transparent text-zinc-600 text-xs border border-transparent hover:border-[var(--solar-line)] focus:border-[var(--brand)] rounded px-1 py-0.5 cursor-pointer focus:outline-none">
                        {(Object.keys(FORMATO_LABEL) as Formato[]).map(f => <option key={f} value={f}>{FORMATO_LABEL[f]}</option>)}
                      </select>
                    </td>
                    <td className="py-2 px-2 text-zinc-600">{p.persona ?? "—"}</td>
                  </tr>
                );
              })}
              {pautasFiltradas.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-zinc-400">Nenhuma pauta com esses filtros.</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <DragDropContext
          onDragEnd={(result: DropResult) => {
            if (!result.destination) return;
            const pautaId = Number(result.draggableId);
            const destColuna = result.destination.droppableId as ColunaPauta;
            const srcColuna  = result.source.droppableId as ColunaPauta;
            // "Fora do quadro" só mostra; não recebe card.
            if ((destColuna as string) === COLUNA_FORA) return;
            if (destColuna !== srcColuna) {
              handleMove(pautaId, destColuna);
            }
          }}
        >
          <div className="flex-1 overflow-x-auto overflow-y-hidden">
            <div
              className="flex gap-4 p-6 h-full"
              style={{ minWidth: `${colunasVisiveis.length * 260 + (colunasVisiveis.length - 1) * 16 + 48}px` }}
            >
              {colunasVisiveis.map(col => {
                // Coluna "Sugestão de Tema" renderiza temas, não pautas.
                const isTemasCol = col.id === "sugestao_tema";
                const isForaCol = (col.id as string) === COLUNA_FORA;
                const cards = isTemasCol
                  ? []  // não há pautas dessa coluna; cards são temas (renderizados abaixo)
                  : isForaCol ? pautasForaDoQuadro
                  : pautasFiltradas.filter(p => p.coluna === col.id);
                const cardsCount = isTemasCol ? temasSugestaoVisiveis.length : cards.length;
                const colSelecionada = cards.length > 0 && cards.every(c => selecionados.has(c.id));
                const hojeISO = new Date(Date.now() - 3 * 3600000).toISOString().slice(0, 10);
                const atrasadas = (isTemasCol || col.id === "publicado" || col.id === "desaprovado") ? 0
                  : cards.filter(c => (c as any).dataPrevista && String((c as any).dataPrevista).slice(0, 10) < hojeISO).length;
                const colapsada = colsColapsadas.has(col.id);
                if (colapsada) {
                  return (
                    <button
                      key={col.id}
                      onClick={() => toggleColColapsada(col.id)}
                      title={`Expandir ${col.label}`}
                      className="w-11 flex-none flex flex-col items-center gap-2 pt-2 pb-3 rounded-xl transition-colors hover:bg-[var(--sidebar-accent)]"
                      style={{ background: "rgba(0,0,0,0.02)" }}
                    >
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: STATUS_COLOR[col.id] ?? col.color }} />
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: "#E7E3DA", color: "var(--solar-ink)" }}>{cardsCount}</span>
                      <span className="text-[11px] font-semibold text-[var(--solar-ink)] whitespace-nowrap mt-1" style={{ fontFamily: "Poppins, sans-serif", writingMode: "vertical-rl", transform: "rotate(180deg)" }}>{col.label}</span>
                    </button>
                  );
                }
                return (
                  <div key={col.id} className="w-60 flex-none flex flex-col">
                    <div className="flex items-center gap-2 mb-3 px-1">
                      {/* HU-059: selecionar todos da coluna */}
                      {modoSelecao && (
                        <button
                          onClick={() => toggleColuna(col.id)}
                          className="w-4 h-4 rounded border-2 flex items-center justify-center shrink-0"
                          style={{
                            borderColor: colSelecionada ? "#0A0A0A" : "#D6D3CD",
                            background: colSelecionada ? "#0A0A0A" : "white",
                          }}
                          title="Selecionar todos desta coluna"
                        >
                          {colSelecionada && (
                            <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                      )}
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: col.color }} />
                      <span className="text-[#0A0A0A] text-xs font-semibold leading-tight" style={{ fontFamily: "Poppins, sans-serif" }}>
                        {col.label}
                      </span>
                      <span
                        className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                        style={{ background: "#E7E3DA", color: "var(--solar-ink)" }}
                      >
                        {cardsCount}
                      </span>
                      {atrasadas > 0 && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0" style={{ background: "#FEE2E2", color: "#DC2626" }} title={`${atrasadas} com data prevista vencida`}>
                          {atrasadas} atras.
                        </span>
                      )}
                      {!modoSelecao && (
                        <button onClick={() => toggleColColapsada(col.id)} title={`Recolher ${col.label}`} className="text-[var(--solar-mute)] hover:text-[var(--solar-ink)] transition-colors shrink-0">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                        </button>
                      )}
                    </div>

                    {isTemasCol ? (
                      // Coluna especial: temas não são draggable (não viram pauta sem
                      // aprovar). Cards usam TemaCard com botões Aprovar/Descartar.
                      <div className="flex-1 overflow-y-auto space-y-2.5 pr-1 pb-2">
                        {temasSugestaoVisiveis.length === 0 && (
                          <p className="text-xs text-zinc-400 italic px-2 py-3 text-center">
                            Nenhum tema em sugestão
                          </p>
                        )}
                        {temasSugestaoVisiveis.map((t: any) => (
                          <TemaCard
                            key={t.id}
                            tema={t}
                            filhos={t.codigo ? (filhosTemaMap[String(t.codigo).trim().toLowerCase()] ?? 0) : 0}
                            onAprovar={id => aprovarTemaMutation.mutate({ id })}
                            onDescartar={id => setTemaRecusaAlvo({ id, titulo: (temasSugestao.find((x: any) => x.id === id)?.tema) ?? "" })}
                            onClick={id => setTemaModal({ id })}
                          />
                        ))}
                        <button
                          onClick={() => setTemaModal("new")}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-xs transition-colors"
                          style={{
                            border: "2px dashed #A78BFA",
                            color: "#7C3AED",
                            fontFamily: "Poppins, sans-serif",
                          }}
                        >
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                          </svg>
                          Novo tema
                        </button>
                      </div>
                    ) : (
                    <Droppable droppableId={col.id}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className="flex-1 overflow-y-auto space-y-2.5 pr-1 pb-2 rounded-xl transition-colors"
                          style={{ minHeight: "60px", background: snapshot.isDraggingOver ? "#EDEBE6" : "transparent" }}
                        >
                          {isForaCol && (
                            <p className="text-[11px] leading-snug px-2 py-2 rounded-lg" style={{ background: "#FEF3F2", color: "#B42318" }}>
                              Cards numa etapa que este quadro não usa. Arraste para uma etapa ou troque a etapa no card.
                            </p>
                          )}
                          {cards.map((pauta, index) => (
                            <Draggable key={pauta.id} draggableId={String(pauta.id)} index={index}>
                              {(drag, dragSnap) => (
                                <div
                                  ref={drag.innerRef}
                                  {...drag.draggableProps}
                                  {...drag.dragHandleProps}
                                  style={{
                                    ...drag.draggableProps.style,
                                    opacity: dragSnap.isDragging ? 0.85 : 1,
                                  }}
                                >
                                  <PautaCard
                                    pauta={pauta as Pauta}
                                    thumb={thumbByPauta[pauta.id]}
                                    onMove={handleMove}
                                    onDelete={handleDelete}
                                    onClick={p => setDetalhe(p)}
                                    onVoltarSugestao={id => voltarSugestaoMutation.mutate({ id })}
                                    selecionavel={modoSelecao}
                                    selecionado={selecionados.has(pauta.id)}
                                    onToggleSelecao={toggleSelecao}
                                    childCount={childCountMap[pauta.id] ?? 0}
                                    onDuplicar={p => setDuplicarAlvo(p)}
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}

                          {isForaCol ? null : quickAddCol === col.id ? (
                            <input
                              autoFocus
                              value={quickAddText}
                              onChange={e => setQuickAddText(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === "Enter" && quickAddText.trim()) {
                                  quickAddMutation.mutate({ clientId, titulo: quickAddText.trim(), coluna: col.id as ColunaPauta, rede: "linkedin", formato: "post" } as any);
                                  setQuickAddText("");
                                } else if (e.key === "Escape") { setQuickAddCol(null); setQuickAddText(""); }
                              }}
                              onBlur={() => { if (!quickAddText.trim()) setQuickAddCol(null); }}
                              placeholder="Título e Enter…"
                              className="w-full px-3 py-2.5 rounded-xl text-xs bg-white focus:outline-none"
                              style={{ border: "1px solid var(--brand)", fontFamily: "Poppins, sans-serif" }}
                            />
                          ) : (
                            <button
                              onClick={() => { setQuickAddCol(col.id as ColunaPauta); setQuickAddText(""); }}
                              className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs transition-colors hover:bg-[var(--sidebar-accent)]"
                              style={{ border: "2px dashed var(--solar-line)", color: "var(--solar-mute)", fontFamily: "Poppins, sans-serif" }}
                            >
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                              </svg>
                              Adicionar
                            </button>
                          )}
                        </div>
                      )}
                    </Droppable>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </DragDropContext>
      )}

      {novaColuna && (
        <NovaPautaModal
          colunaInicial={novaColuna}
          clientId={clientId}
          onSave={() => refetch()}
          onClose={() => setNovaColuna(null)}
        />
      )}

      {detalhe && (
        <PautaDetailModal
          key={detalhe.id}
          pauta={detalhe}
          onClose={() => setDetalhe(null)}
          onSave={handleDetailSave}
          onDelete={id => { handleDelete(id); setDetalhe(null); }}
          onHardDelete={id => { hardDeletePautaMutation.mutate({ id }); setDetalhe(null); }}
          onVoltarSugestao={id => { voltarSugestaoMutation.mutate({ id }); setDetalhe(null); }}
          onOpenRelated={p => setDetalhe(p)}
        />
      )}

      {duplicarAlvo && (
        <DuplicarModal
          key={duplicarAlvo.id}
          pauta={duplicarAlvo}
          onCancel={() => setDuplicarAlvo(null)}
          onDone={novo => { setDuplicarAlvo(null); refetch(); setDetalhe(novo); }}
        />
      )}

      {desaprovarAlvo && (
        <DesaprovarModal
          titulo={desaprovarAlvo.titulo}
          pending={desaprovarMutation.isPending}
          onCancel={() => setDesaprovarAlvo(null)}
          onConfirm={motivo => desaprovarMutation.mutate({ id: desaprovarAlvo.id, motivo })}
        />
      )}

      {temaRecusaAlvo && (
        <DesaprovarModal
          titulo={temaRecusaAlvo.titulo}
          pending={temaDesaprovarMutation.isPending}
          onCancel={() => setTemaRecusaAlvo(null)}
          onConfirm={motivo => temaDesaprovarMutation.mutate({ id: temaRecusaAlvo.id, motivo })}
        />
      )}

      {importOpen && isRazUser && (
        <ImportarExportarModal
          clientId={clientId}
          onClose={() => setImportOpen(false)}
          onImported={() => { refetch(); refetchTemas(); }}
        />
      )}

      {lixeiraOpen && (
        <LixeiraModal
          clientId={clientId}
          onClose={() => setLixeiraOpen(false)}
          onRestored={() => { refetch(); refetchTemas(); }}
        />
      )}

      {temaModal && (
        <TemaFormModal
          mode={temaModal}
          clientId={clientId}
          onClose={() => setTemaModal(null)}
          onSaved={() => refetchTemas()}
        />
      )}
    </div>
    </ColumnsCtx.Provider>
  );
}
